/*
 * Carbisdale - Renderer-Modul
 *
 * Verantwortlich für das Zeichnen auf dem Canvas.
 * Unterstützt verschiedene Display Modes und rendert sowohl
 * Text-Zeichen als auch Box-Drawing Characters.
 *
 * Verantwortlichkeiten:
 * - Canvas erstellen und verwalten
 * - Character-Dimensionen messen
 * - Grid rendern (Zeichen für Zeichen)
 * - Box-Drawing Characters als Linien zeichnen
 * - Glow-Effekt anwenden
 * - Cursor rendern (invertiert)
 * - Overlay-Layer rendern
 */

import {
    DISPLAY_MODES,
    DisplayMode,
    DEFAULT_DISPLAY_MODE,
    FONTS,
    DEFAULT_FONT,
    FONT_SIZE,
    LINE_HEIGHT_Q,
    LINE_HEIGHT_FACTOR,
    BOX_CHARS_SET,
    DOUBLE_BOX_CHARS_SET,
    BoxChars,
    STYLES,
    Style,
    Intensity,
    PaneId,
    PaneState,
    FontCategory,
    COLORS
} from './config.js'

import { ControlType, StationType } from './layout.js'
import {
    buildMetricsCache,
    computeStringLayout,
    REFERENCE_SIZE
} from './GlyphMetrics.js'
import { charDisplayWidth } from './TextProcessor.js'
import { buildMonoShiftedPositions } from './textGeometry.js'

/**
 * Mischt zwei Hex-Farben linear im RGB-Raum.
 * @param {string} fgHex  Vordergrundfarbe (#rrggbb)
 * @param {string} bgHex  Hintergrundfarbe (#rrggbb)
 * @param {number} t      Anteil des Hintergrunds (0=fg, 1=bg). 0.5 = 50/50-Mix.
 * @returns {string} Resultat als #rrggbb
 */
export function mixHexColors(fgHex, bgHex, t) {
    if (typeof fgHex !== 'string' || typeof bgHex !== 'string') return fgHex
    const fg = fgHex.startsWith('#') ? fgHex.slice(1) : fgHex
    const bg = bgHex.startsWith('#') ? bgHex.slice(1) : bgHex
    if (fg.length !== 6 || bg.length !== 6) return fgHex
    const fr = parseInt(fg.slice(0, 2), 16)
    const fgG = parseInt(fg.slice(2, 4), 16)
    const fb = parseInt(fg.slice(4, 6), 16)
    const br = parseInt(bg.slice(0, 2), 16)
    const bgG = parseInt(bg.slice(2, 4), 16)
    const bb = parseInt(bg.slice(4, 6), 16)
    const r = Math.round(fr * (1 - t) + br * t)
    const g = Math.round(fgG * (1 - t) + bgG * t)
    const b = Math.round(fb * (1 - t) + bb * t)
    return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0')
}

/**
 * Renderer-Klasse
 *
 * Kapselt alle Canvas-Operationen und kennt die Pixel-Dimensionen
 * eines einzelnen Zeichens im aktuellen Font.
 */
export class Renderer {
    /**
     * Erstellt einen neuen Renderer.
     *
     * @param {HTMLElement} container - DOM-Element in das der Canvas eingefügt wird
     */
    constructor(container) {
        this.container = container
        this.canvas = null
        this.ctx = null

        // Cursor-Layer: separates Canvas nur für den Cursor
        this._cursorCanvas = null
        this._cursorCtx = null

        // Dirty-Flag: wird der Border im nächsten Frame neu gezeichnet? Alles was
        // Pane-Bounds/Titel/Controls/Uhr berührt, setzt das Flag. renderBorderOnMain
        // läuft genau dann durch.
        this._borderDirty = true

        // Character-Dimensionen (werden nach Font-Load berechnet)
        this.charWidth = 0
        this.charHeight = 0

        // Grid-Offset in device pixels (HW-Pixel). Zentriert das Grid im Canvas:
        // Übriggebliebene Pixel (window.innerWidth - cols*charWidth) werden in
        // zwei gleich breite Streifen links/rechts (und oben/unten) geteilt.
        // Wird via ctx.setTransform(dpr,0,0,dpr,offsetX,offsetY) appliziert →
        // alle Draw-Calls landen automatisch in der Grid-Mitte.
        this._gridOffsetX = 0
        this._gridOffsetY = 0

        // Aktuelle Einstellungen
        this.fontSize = FONT_SIZE.default
        this.currentFont = DEFAULT_FONT
        this.currentDisplayMode = DEFAULT_DISPLAY_MODE

        // Aktive Farben (aus Display Mode)
        this._updateColors()

        // Style-Cache: _resolveStyle()-Ergebnisse pro styleName (invalidiert bei Mode-Wechsel)
        this._styleCache = new Map()

        // Font-State-Cache: vermeidet redundante ctx.font-Zuweisungen
        this._lastFont = null

        // Shadow-State-Cache: vermeidet redundante ctx.shadowBlur-Zuweisungen
        this._lastShadowBlur = undefined

        // Tab-Leiste Referenzen (werden von main.js gesetzt)
        this._tabBarRenderer = null
        this._tabManager = null
        this._projectTitles = null

        // Glyph-Atlas: vorgerenderte Zeichen als OffscreenCanvas (Grid, monospaced)
        // Key: `${char}|${color}|${isBold}|${isItalic}|${hasGlow}`
        this._glyphAtlas = new Map()

        // Proportionaler Glyph-Atlas: variable Breiten, nativer Italic-Font
        // Key: `${char}|${color}|${isBold}|${isItalic}|${hasGlow}|${heightQ}`
        this._propGlyphAtlas = new Map()

        // Proportional-Font-State
        this._metricsCache = null    // GlyphMetricsCache aus GlyphMetrics.js
        this._fontRefs = null        // { xHeight, capHeight, baseline, ascenderHeight }
        this._scaleFactor = 1        // _gridFontSize / REFERENCE_SIZE
        this._gridFontSize = FONT_SIZE.default  // Effektive Font-Größe für Grid (Prop: 2/3 von fontSize)

        // System-Font-State (fuer Garamond: NotoSans bei 4QL statt EBGaramond)
        this._systemMetricsCache = null
        this._systemScaleFactor = 1
    }

    /**
     * Prueft ob der aktuelle Mode ein Nerd-Terminal ist (Phosphor-Glow, kein Colored).
     * Gilt fuer ULTRA_NERD und REGULAR_NERD.
     */
    isNerdMode() {
        const mode = DISPLAY_MODES[this.currentDisplayMode]
        return mode && !!mode.isNerd
    }

    /**
     * Prueft ob der aktuelle Mode einen hellen Hintergrund hat.
     */
    isLightBackground() {
        const mode = DISPLAY_MODES[this.currentDisplayMode]
        return mode && !mode.isColored && mode.background === '#ffffff'
    }

    /**
     * Prüft ob der aktuelle Font proportional ist.
     * @returns {boolean}
     */
    isProportional() {
        const fontConfig = FONTS[this.currentFont]
        return fontConfig && fontConfig.category === FontCategory.PROPORTIONAL
    }

    /**
     * Liefert die effektive Viewport-Breite. Drei Quellen, in Reihenfolge:
     *  - `this.container.clientWidth` — respektiert `body { padding: env(safe-area-inset-*) }`,
     *    also den iPhone-Notch-Bereich in Landscape. Bevorzugt, wenn schon
     *    gelayoutet (Cold-Start vor erstem Layout-Pass: clientWidth = 0).
     *  - `visualViewport.width` — Soft-Keyboard / Pinch-Zoom reduzieren das.
     *  - `window.innerWidth` — Default-Fallback.
     *
     * Der `Math.min(container, visualViewport)`-Override ist wichtig: bei
     * eingeblendeter iOS-Tastatur bleibt `container.clientWidth` voll (Body-
     * Hoehe aendert sich nicht), aber `visualViewport.width` schrumpft. Wir
     * wollen die System-Tastatur respektieren.
     * @returns {number}
     */
    _viewportWidth() {
        const containerW = this.container ? this.container.clientWidth : 0
        const vvW = (typeof window !== 'undefined' && window.visualViewport)
            ? window.visualViewport.width
            : (typeof window !== 'undefined' ? window.innerWidth : 0)
        if (containerW > 0) return Math.min(containerW, vvW || containerW)
        return vvW
    }

    /**
     * Liefert die effektive Viewport-Hoehe. Logik analog zu `_viewportWidth`:
     * container.clientHeight respektiert das Body-Padding (iPhone Notch /
     * Home-Indicator), visualViewport.height schrumpft bei eingeblendeter
     * Soft-Keyboard. `Math.min` der beiden liefert das, was wir wirklich an
     * Pixeln zur Verfuegung haben.
     * @returns {number}
     */
    _viewportHeight() {
        const containerH = this.container ? this.container.clientHeight : 0
        const vvH = (typeof window !== 'undefined' && window.visualViewport)
            ? window.visualViewport.height
            : (typeof window !== 'undefined' ? window.innerHeight : 0)
        if (containerH > 0) return Math.min(containerH, vvH || containerH)
        return vvH
    }

    /**
     * Gibt den GlyphMetrics-Cache zurück (für proportionale Layout-Berechnung).
     * Bei heightQ <= 4 und System-Font wird der System-Cache zurückgegeben.
     * @param {number} [heightQ] - Quarterline-Höhe (optional)
     * @returns {GlyphMetricsCache|null}
     */
    getGlyphMetricsCache(heightQ) {
        if (heightQ != null) return this._activeMetrics(heightQ).cache
        return this._metricsCache
    }

    /**
     * Gibt den Skalierungsfaktor zurück (fontSize / REFERENCE_SIZE).
     * Bei heightQ <= 4 und System-Font wird der System-Scale zurückgegeben.
     * @param {number} [heightQ] - Quarterline-Höhe (optional)
     * @returns {number}
     */
    getScaleFactor(heightQ) {
        if (heightQ != null) return this._activeMetrics(heightQ).scaleFactor
        return this._scaleFactor
    }

    /**
     * Gibt MetricsCache und FontConfig fuer eine bestimmte heightQ zurueck.
     * Bei 4QL und vorhandenem System-Font (z.B. Garamond→NotoSans) wird
     * der System-Cache verwendet, sonst der Body-Cache.
     */
    _activeMetrics(heightQ = 4) {
        if (heightQ <= 4 && this._systemMetricsCache) {
            const fontConfig = FONTS[this.currentFont]
            return {
                cache: this._systemMetricsCache,
                scaleFactor: this._systemScaleFactor,
                fontConfig: FONTS[fontConfig.systemFont]
            }
        }
        return {
            cache: this._metricsCache,
            scaleFactor: this._scaleFactor,
            fontConfig: FONTS[this.currentFont]
        }
    }

    // === heightQ-basierte Berechnungen (variable Zeilenhöhen im Proportional-Modus) ===

    /**
     * Skalierungsfaktor für eine bestimmte Quarterline-Höhe.
     * @param {number} heightQ - Zeilenhöhe in Quarterlines (4 = Basis)
     * @returns {number}
     */
    scaleFactorForHeightQ(heightQ) {
        return this._scaleFactor * heightQ / 4
    }

    /**
     * Font-Größe in Pixel für eine bestimmte Quarterline-Höhe.
     * Basiert auf _gridFontSize (4-QL = _gridFontSize, 6-QL ≈ fontSize).
     * @param {number} heightQ
     * @returns {number}
     */
    fontSizeForHeightQ(heightQ) {
        return this._gridFontSize * heightQ / 4
    }

    /**
     * Pixel-Höhe einer Zeile für eine bestimmte Quarterline-Höhe.
     * @param {number} heightQ
     * @returns {number}
     */
    charHeightForHeightQ(heightQ) {
        return this.charHeight * heightQ / 4
    }

    /**
     * Pixel-Breite eines Absatzmarkers (> oder <) im Proportional-Modus.
     * Marker werden standardmäßig in NORMAL-Größe (6QL) gerendert und brauchen
     * deshalb mehr Platz als ein Grid-charWidth. Note/Info-Marker laufen in 4QL
     * (System-Font, wie Separator-Labels) und reservieren entsprechend weniger.
     * @param {number} heightQ - Marker-Höhe in Quarter-Lines (Default NORMAL = 6QL)
     * @returns {number}
     */
    markerWidthPx(heightQ = LINE_HEIGHT_Q.NORMAL) {
        return this.charWidth * heightQ / 4
    }

    /**
     * Text-Höhe (ohne Zeilenabstand) für eine bestimmte Quarterline-Höhe.
     * Für Selection-Hintergründe, Cursor etc.
     * @param {number} heightQ
     * @returns {number}
     */
    textHeightForHeightQ(heightQ) {
        const localCharH = this.charHeight * heightQ / 4
        const localFontSize = this._gridFontSize * heightQ / 4
        return Math.min(localCharH, localFontSize * 1.1)
    }

    /**
     * Idealer (ungekappter) Baseline-Shift in Pixeln, damit Sekundärtext (4QL)
     * exakt auf der Baseline der größeren Hauptzeile sitzt. Reine Geometrie.
     * @param {number} mainHeightQ
     * @returns {number}
     */
    _secondaryBaselineIdealPx(mainHeightQ) {
        const SECONDARY = LINE_HEIGHT_Q.SECONDARY
        if (mainHeightQ <= SECONDARY) return 0
        return (this.fontSizeForHeightQ(mainHeightQ) - this.fontSizeForHeightQ(SECONDARY)) * 0.85
    }

    /**
     * Vertikale Luft einer 4QL-Sekundärzelle (Zellhöhe − Texthöhe). So weit darf der
     * Text BEIM RENDERN nach unten geschoben werden, ohne seine Grid-Zelle zu verlassen.
     * @returns {number}
     */
    _secondaryCellSlackPx() {
        const SECONDARY = LINE_HEIGHT_Q.SECONDARY
        return Math.max(0, this.charHeightForHeightQ(SECONDARY) - this.textHeightForHeightQ(SECONDARY))
    }

    /**
     * Baseline-Shift (Pixel) für Sekundärtext, der BEIM RENDERN angewendet wird —
     * gekappt auf die Luft der eigenen 4QL-Zelle. Bleibt der Text in seiner Zelle,
     * stimmen Render- und Grid-Position überein (kein Scroll-„Hereinspringen").
     *
     * Die volle Baseline-Ausrichtung entsteht aus ZWEI Teilen: dieser gekappte Shift
     * (Render-Layer) PLUS `secondaryBaselineExcessPx` (ins Per-Pane-Y-Grid gebacken).
     * Der Überschuss steckt im Grid, weil dort Render = Grid gilt → er erzeugt kein
     * Artefakt. Bei Normaltext (6QL) passt der ideale Shift komplett in die Zellen-Luft
     * → Überschuss 0, alles läuft wie zuvor über den Render-Layer. Nur Headings
     * schieben den Überschuss ins Grid. Single-Source für OutlineView/ContextView/
     * EditorView (Render-Note, Render-Info, Cursor/Klick) — alle rechnen identisch.
     * @param {number} mainHeightQ - heightQ der zugehörigen Hauptzeile
     * @returns {number} Pixel-Shift (>= 0)
     */
    secondaryBaselineShiftPx(mainHeightQ) {
        return Math.min(this._secondaryBaselineIdealPx(mainHeightQ), this._secondaryCellSlackPx())
    }

    /**
     * Der Anteil des idealen Baseline-Shifts, der NICHT in die 4QL-Zelle passt und
     * deshalb ins Per-Pane-Y-Grid gebacken wird (siehe `EditorView._buildSlotQuarterY`).
     * Nur bei Headings > 0; bei Normaltext/Separatoren 0. Render-Shift + Excess =
     * idealer (voll baseline-ausgerichteter) Shift.
     * @param {number} mainHeightQ - heightQ der zugehörigen Hauptzeile
     * @returns {number} Pixel-Überschuss (>= 0)
     */
    secondaryBaselineExcessPx(mainHeightQ) {
        // NUR Headings (> NORMAL) schieben Überschuss ins Grid. Body-Text (≤ 6QL)
        // bekommt KEINEN Grid-Excess. Sonst startet die Note-Zelle (und der daran
        // gekoppelte Outline-Tree-Connector) um excessQ tiefer → an 0-Spacing-Slot-
        // Grenzen (Listen, Bullets) klafft eine excessQ-Lücke in der vertikalen Linie.
        // Das passiert bei Fonts mit kleinem lineHeightFactor (z.B. Worker, lhf 1.2 →
        // charHeight 16), wo der ideale 6QL-Shift die 4QL-Zellen-Luft übersteigt; bei
        // Garamond (lhf 1.4 → charHeight 20) liegt ideal(6) knapp darunter → excess 0,
        // deshalb trat es dort nicht auf. Normale Absätze haben Spacing → der Bridge-
        // Connector maskiert die Lücke; nur 0-Spacing-Listen zeigen sie. Headings
        // haben immer Spacing, also dort kein sichtbarer Lücken-Effekt.
        if (mainHeightQ <= LINE_HEIGHT_Q.NORMAL) return 0
        return Math.max(0, this._secondaryBaselineIdealPx(mainHeightQ) - this.secondaryBaselineShiftPx(mainHeightQ))
    }

    /**
     * Löst die Hintergrundfarbe für ein Such-Highlight auf.
     * @param {string} highlightType - 'match' oder 'current'
     * @returns {string|null}
     */
    _resolveSearchHighlightColor(highlightType) {
        if (this.isNerdMode()) {
            if (highlightType === 'current') {
                return STYLES[Style.EDITOR_SELECTION].nerdBackground
            }
            return null
        }

        const hlStyle = highlightType === 'current'
            ? STYLES[Style.SEARCH_CURRENT_MATCH]
            : STYLES[Style.SEARCH_MATCH]

        if (!hlStyle) return null

        if (this.colors.isColored) {
            return this.colors.isDarkMode ? hlStyle.darkBackground : hlStyle.lightBackground
        }
        return this.isLightBackground()
            ? hlStyle.lightBackground
            : hlStyle.darkBackground
    }

    /**
     * Aktualisiert die Farb-Referenzen basierend auf dem aktuellen Display Mode.
     */
    _updateColors() {
        this.colors = DISPLAY_MODES[this.currentDisplayMode]
        // Body-BG mitziehen: Der zentrierte Grid-Bereich hinterlaesst durch
        // Glyphen-Dimensionierung schmale Streifen am Rand des Viewports.
        // Diese sehen bei hellen Modes als schwarzer "Trauerrand" hervor —
        // mit Mode-Background sind sie unsichtbar.
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.backgroundColor = this.colors.background
        }
    }

    /**
     * Ermittelt die Farbe und Textattribute für einen Style im aktuellen Display Mode.
     *
     * Bei monochromen Modi (NERD, DARK, LIGHT): Nutzt intensity aus dem Style
     * Bei colored Modi (DARK_COLORED, LIGHT_COLORED): Nutzt darkColor/lightColor
     *
     * @param {string} styleName - Name des Styles aus Style enum
     * @returns {{color: string, isBold: boolean, isItalic: boolean, isStrikethrough: boolean, isUnderline: boolean, hasGlow: boolean, backgroundColor: string|null}}
     */
    _resolveStyle(styleName) {
        // Cache-Lookup (O(1) statt erneute Berechnung)
        const cached = this._styleCache.get(styleName)
        if (cached) return cached

        // Compound-Styles: "EDITOR_BOLD+ST+UL+BG" → Base "EDITOR_BOLD" + Suffixe
        let baseStyleName = styleName
        let compoundST = false
        let compoundUL = false
        let compoundBG = false
        if (styleName.includes('+')) {
            const parts = styleName.split('+')
            baseStyleName = parts[0]
            for (let i = 1; i < parts.length; i++) {
                if (parts[i] === 'ST') compoundST = true
                if (parts[i] === 'UL') compoundUL = true
                if (parts[i] === 'BG') compoundBG = true
            }
        }

        const style = STYLES[baseStyleName] || STYLES[Style.DEFAULT]
        const modeConfig = this.colors

        let color
        let backgroundColor = null

        if (modeConfig.isColored) {
            // Background-Bereich: Sepia-Farben verwenden (wenn vorhanden)
            if (compoundBG && style.darkColorBg) {
                color = modeConfig.isDarkMode ? style.darkColorBg : style.lightColorBg
            } else if (this.currentDisplayMode === DisplayMode.WORKER_DARK && style.workerDarkColor) {
                // Mode-spezifischer Override (z.B. Control-Icons mit getauschten
                // Normal/Hover-Farben in Worker Dark)
                color = style.workerDarkColor
            } else {
                color = modeConfig.isDarkMode ? style.darkColor : style.lightColor
            }
            if (style.darkBackground || style.lightBackground) {
                backgroundColor = modeConfig.isDarkMode ? style.darkBackground : style.lightBackground
            }
        } else {
            // Background-Bereich: gedämpftere Intensität in monochromen Modi
            const intensity = (compoundBG && style.intensityBg) ? style.intensityBg : style.intensity
            color = modeConfig.foreground[intensity]
            if (this.isNerdMode() && style.nerdBackground) {
                backgroundColor = style.nerdBackground
                color = style.nerdForeground || this.colors.background
            } else if (style.darkBackground) {
                backgroundColor = this.isLightBackground()
                    ? style.lightBackground
                    : style.darkBackground
            }
        }

        // Strikethrough ist farblich dominant — überschreibt Base-Style-Farbe
        if (compoundST) {
            const stStyle = STYLES[Style.EDITOR_STRIKETHROUGH]
            if (modeConfig.isColored) {
                color = modeConfig.isDarkMode ? stStyle.darkColor : stStyle.lightColor
            } else {
                color = modeConfig.foreground[stStyle.intensity]
            }
        }

        // Dim-to-Background: Style mischt sich Anteil x mit Pane-Background.
        // Mode-unabhaengig — funktioniert ueber alle Display-Modes (Phosphor, Sepia, …).
        if (style.dimToBackground && color) {
            color = mixHexColors(color, modeConfig.background, style.dimToBackground)
        }

        const hasGlow = !compoundST && !compoundBG && !style.dimToBackground && style.intensity === Intensity.HIGH && modeConfig.glow.color !== null

        const result = {
            color,
            isBold: style.isBold || false,
            isItalic: style.isItalic || false,
            isStrikethrough: (style.isStrikethrough || false) || compoundST,
            isUnderline: (style.isUnderline || false) || compoundUL,
            isMono: style.isMono || false,
            hasGlow,
            backgroundColor
        }

        this._styleCache.set(styleName, result)
        return result
    }

    /**
     * Initialisiert den Canvas und berechnet die Character-Dimensionen.
     * Wartet explizit auf das Laden unserer Custom Fonts.
     *
     * @returns {Promise} Resolved wenn Canvas bereit ist
     */
    async init() {
        // Canvas erstellen
        this.canvas = document.createElement('canvas')
        this.canvas.id = 'screen-canvas'
        this.ctx = this.canvas.getContext('2d')
        // Kein Smoothing: Glyphen bringen eigenes Anti-Aliasing mit (Glyph-Atlas),
        // doppeltes Smoothing wuerde Text unnoetig weichzeichnen
        this.ctx.imageSmoothingEnabled = false

        // Cursor-Layer: transparentes Canvas über dem Hauptcanvas
        this._cursorCanvas = document.createElement('canvas')
        this._cursorCanvas.id = 'cursor-canvas'
        this._cursorCtx = this._cursorCanvas.getContext('2d')

        // In Container einfügen (Cursor-Canvas über Hauptcanvas)
        this.container.appendChild(this.canvas)
        this.container.appendChild(this._cursorCanvas)

        // Explizit auf unsere Fonts warten
        await this._waitForFonts()

        // Canvas-Größe auf Viewport setzen
        this._updateCanvasSize()

        // Character-Dimensionen messen
        this._measureCharacter()

        // Proportional: Metriken-Cache aufbauen (wichtig bei Reload mit gespeichertem Prop-Font)
        if (this.isProportional()) {
            this._buildMetricsCache()
        }
    }

    /**
     * Wartet bis alle benötigten Fonts geladen sind.
     * Verwendet document.fonts.load() für explizites Laden.
     *
     * @returns {Promise}
     */
    async _waitForFonts(font) {
        const fontConfig = FONTS[font || this.currentFont]
        const testString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

        try {
            // Explizit Font-Varianten laden
            const loadPromises = [
                document.fonts.load(`${FONT_SIZE.default}px ${fontConfig.family}`, testString),
                document.fonts.load(`bold ${FONT_SIZE.default}px ${fontConfig.familyBold}`, testString)
            ]

            // Grid-Companion laden (wenn proportional, z.B. NotoSansMono für Grid)
            if (fontConfig.gridFamily && fontConfig.gridFamily !== fontConfig.family) {
                loadPromises.push(
                    document.fonts.load(`${FONT_SIZE.default}px ${fontConfig.gridFamily}`, testString)
                )
            }
            if (fontConfig.gridFamilyBold && fontConfig.gridFamilyBold !== fontConfig.familyBold) {
                loadPromises.push(
                    document.fonts.load(`bold ${FONT_SIZE.default}px ${fontConfig.gridFamilyBold}`, testString)
                )
            }

            // Proportional: auch Italic-Varianten laden
            if (fontConfig.familyItalic) {
                loadPromises.push(
                    document.fonts.load(`${FONT_SIZE.default}px ${fontConfig.familyItalic}`, testString)
                )
            }
            if (fontConfig.familyBoldItalic) {
                loadPromises.push(
                    document.fonts.load(`bold ${FONT_SIZE.default}px ${fontConfig.familyBoldItalic}`, testString)
                )
            }

            // System-Font laden (z.B. NotoSans fuer Garamond-Labels)
            if (fontConfig.systemFont && FONTS[fontConfig.systemFont]) {
                const sysConfig = FONTS[fontConfig.systemFont]
                loadPromises.push(
                    document.fonts.load(`${FONT_SIZE.default}px ${sysConfig.family}`, testString)
                )
                if (sysConfig.familyBold) {
                    loadPromises.push(
                        document.fonts.load(`bold ${FONT_SIZE.default}px ${sysConfig.familyBold}`, testString)
                    )
                }
            }

            await Promise.all(loadPromises)

            // Zusätzlich auf document.fonts.ready warten für Sicherheit
            await document.fonts.ready

            // Kleine Verzögerung für Safari, der manchmal noch nicht ganz fertig ist
            await new Promise(resolve => setTimeout(resolve, 50))

        } catch (error) {
            console.warn('Font-Loading fehlgeschlagen, verwende Fallback:', error)
            // Bei Fehler trotzdem weitermachen mit System-Font
            await document.fonts.ready
        }
    }

    /**
     * Setzt die Canvas-Größe auf die aktuelle Viewport-Größe.
     * Berücksichtigt devicePixelRatio für scharfe Darstellung auf HiDPI-Displays.
     */
    _updateCanvasSize() {
        const dpr = window.devicePixelRatio || 1
        const width = this._viewportWidth()
        const height = this._viewportHeight()

        // Canvas-Größe in Pixeln (berücksichtigt HiDPI).
        // Zuweisung an canvas.width setzt den Canvas-Inhalt auf transparent zurück —
        // auch bei gleichem Wert (HTML5 Canvas Spec). Das ist der gewünschte
        // Clear-Effekt beim Zoomen.
        this.canvas.width = width * dpr
        this.canvas.height = height * dpr

        // CSS-Größe bleibt bei Viewport-Größe (integer CSS-Pixel → scharfes Compositing)
        this.canvas.style.width = width + 'px'
        this.canvas.style.height = height + 'px'

        // Cursor-Canvas analog
        if (this._cursorCanvas) {
            this._cursorCanvas.width = width * dpr
            this._cursorCanvas.height = height * dpr
            this._cursorCanvas.style.width = width + 'px'
            this._cursorCanvas.style.height = height + 'px'
        }

        // Transform (HiDPI-Skalierung + Grid-Zentrierung) zentral anwenden
        this._applyGridTransform()

        // Smoothing aus: Glyphen bringen eigenes AA mit (canvas.width Reset setzt State zurueck)
        this.ctx.imageSmoothingEnabled = false

        // Border muss nach Canvas-Resize neu gezeichnet werden
        this._borderDirty = true
    }

    /**
     * Setzt die Canvas-Transforms (Main + Cursor) so, dass das Grid mittig
     * im Viewport landet. Offset wird in GANZEN device pixels berechnet und
     * rundet; damit liegen alle Draw-Calls pixel-scharf auf dem HW-Grid —
     * kein CSS-Compositing-Blur, keine Sub-Pixel-Interpolation in drawImage.
     * Bei noch-nicht-gemessenem Font (charWidth=0) bleibt der Offset auf 0.
     */
    _applyGridTransform() {
        const dpr = window.devicePixelRatio || 1
        if (this.charWidth > 0 && this.charHeight > 0) {
            const vpW = this._viewportWidth()
            const vpH = this._viewportHeight()
            const cols = Math.floor(vpW / this.charWidth)
            const rows = Math.floor(vpH / this.charHeight)
            const gridCssW = cols * this.charWidth
            const gridCssH = rows * this.charHeight
            this._gridOffsetX = Math.round((vpW - gridCssW) / 2 * dpr)
            this._gridOffsetY = Math.round((vpH - gridCssH) / 2 * dpr)
        } else {
            this._gridOffsetX = 0
            this._gridOffsetY = 0
        }
        this.ctx.setTransform(dpr, 0, 0, dpr, this._gridOffsetX, this._gridOffsetY)
        if (this._cursorCtx) {
            this._cursorCtx.setTransform(dpr, 0, 0, dpr, this._gridOffsetX, this._gridOffsetY)
        }
    }

    /**
     * Misst die Breite und Höhe eines Zeichens im aktuellen Font.
     * Monospace-Fonts haben für alle Zeichen die gleiche Breite.
     */
    _measureCharacter() {
        // Proportional: Grid-Font kleiner (2/3), damit 6-QL-Normaltext ≈ Mono-Textgröße
        // und 1.5x mehr Grid-Zeilen auf den Bildschirm passen
        this._gridFontSize = this.isProportional()
            ? Math.round(this.fontSize * 2 / 3)
            : this.fontSize

        // Font setzen (Grid-Font für Borders, Menüs, Overlays)
        this.ctx.font = this._getFontString(false)

        // Breite messen (bei Monospace ist jedes Zeichen gleich breit)
        const metrics = this.ctx.measureText('M')
        this.charWidth = metrics.width

        // Höhe aus Grid-Font-Größe und per-Font Line-Height-Faktor berechnen
        // Auf Vielfaches von 4 runden — Quarter-Spacing braucht ganzzahlige Viertel-Pixel
        const fontConfig = FONTS[this.currentFont]
        const lhFactor = (fontConfig && fontConfig.lineHeightFactor) || LINE_HEIGHT_FACTOR
        this.charHeight = Math.round(this._gridFontSize * lhFactor / 4) * 4

        // Vertikaler Versatz: Font-Block zentriert in der Zelle platzieren
        // Bei lineHeightFactor > 1.0 wird der Extra-Raum gleichmäßig oben/unten verteilt
        this._verticalPad = (this.charHeight - this._gridFontSize) / 2

        // Strikethrough- und Underline-Position relativ zur Grid-Font-Baseline
        const xMetrics = this.ctx.measureText('x')
        const xHeight = xMetrics.actualBoundingBoxAscent || this._gridFontSize * 0.52
        const baselineY = this._verticalPad + this._gridFontSize * 0.85
        this._strikethroughY = baselineY - xHeight * 0.5
        this._underlineY = baselineY + this._gridFontSize * 0.1

        // Texthöhe ohne Zeilenabstand (für Selection, Marker-Hintergründe etc.)
        this._textHeight = Math.min(this.charHeight, this._gridFontSize * 1.1)

        // Diakritik-Überhang messen: wie viel ragt 'Ä' über den 0.85-Baseline-Raum hinaus?
        this._glyphTopPad = this._measureDiacriticOverhang()

        // charWidth/Height haben sich (ggf.) geändert → Grid-Offset neu berechnen,
        // damit das Grid mittig im Viewport bleibt.
        this._applyGridTransform()
    }

    /**
     * Misst den Diakritik-Überhang: wie viele Pixel ragen Umlaut-Punkte (Ä)
     * über den normalen Baseline-Raum (fontSize * 0.85) hinaus?
     * Misst den Grid-Font und bei proportionalem Modus auch den Prop-Font,
     * nimmt das Maximum.
     *
     * @returns {number} TopPad in logischen Pixeln (0 wenn kein Überhang)
     */
    _measureDiacriticOverhang() {
        const fontSize = this._gridFontSize
        // Raum oberhalb der Baseline = vertikaler Versatz + Font-interne Baseline
        const normalBaseline = this._verticalPad + fontSize * 0.85

        // Großzügige Messfläche — Baseline mittig, genug Platz oberhalb
        const measH = Math.ceil(fontSize * 2)
        const measW = Math.ceil(fontSize * 2)
        const canvas = new OffscreenCanvas(measW, measH)
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctx.textBaseline = 'alphabetic'
        ctx.fillStyle = '#ffffff'

        const measBaseline = fontSize  // genug Raum oberhalb

        // Grid-Font messen
        ctx.font = this._getFontString(false)
        let maxAscent = this._findTopPixel(ctx, 'Ä', measBaseline, measW, measH)

        // Bei proportionalem Font auch diesen messen
        if (this.isProportional()) {
            ctx.clearRect(0, 0, measW, measH)
            ctx.font = this._getProportionalFontStringForSize(false, false, fontSize)
            const propAscent = this._findTopPixel(ctx, 'Ä', measBaseline, measW, measH)
            maxAscent = Math.max(maxAscent, propAscent)
        }

        // Überhang = Ascent minus verfügbarer Raum oberhalb der Baseline
        const pad = Math.max(0, maxAscent - normalBaseline)
        // Auf gerade Zahl aufrunden für saubere Pixel-Ausrichtung
        return Math.ceil(pad / 2) * 2
    }

    /**
     * Findet den obersten Tinten-Pixel eines Zeichens und gibt den Ascent zurück.
     *
     * @param {CanvasRenderingContext2D} ctx - Context mit gesetztem Font
     * @param {string} ch - Zeichen zum Messen
     * @param {number} baseline - Y-Position der Baseline
     * @param {number} w - Canvas-Breite
     * @param {number} h - Canvas-Höhe
     * @returns {number} Ascent in Pixeln (Abstand von Baseline zum obersten Pixel)
     */
    _findTopPixel(ctx, ch, baseline, w, h) {
        ctx.clearRect(0, 0, w, h)
        ctx.fillText(ch, 0, baseline)
        const data = ctx.getImageData(0, 0, w, h).data

        for (let y = 0; y < baseline; y++) {
            for (let x = 0; x < w; x++) {
                if (data[(y * w + x) * 4 + 3] > 0) {
                    return baseline - y
                }
            }
        }
        return 0
    }

    /**
     * Erzeugt den Font-String für Grid-Rendering (immer monospaced).
     * Bei proportionalen Fonts wird der Mono-Companion verwendet.
     *
     * @param {boolean} isBold - Fettschrift ja/nein
     * @returns {string} Font-String wie "16px DinaRemasterII"
     */
    _getFontString(isBold) {
        return this._getGridFontStringForSize(isBold, this._gridFontSize)
    }

    /**
     * Erzeugt den Grid-Font-String (monospaced) mit expliziter Font-Größe.
     * Für variable Zeilenhöhen: Code-Blöcke und Inline-Code in heightQ-Größe.
     *
     * @param {boolean} isBold
     * @param {number} fontSize - Font-Größe in Pixel
     * @returns {string}
     */
    _getGridFontStringForSize(isBold, fontSize) {
        const fontConfig = FONTS[this.currentFont]
        const family = isBold
            ? (fontConfig.gridFamilyBold || fontConfig.familyBold)
            : (fontConfig.gridFamily || fontConfig.family)
        const weight = isBold ? 'bold ' : ''
        return `${weight}${fontSize}px ${family}, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"`
    }

    /**
     * Erzeugt den Font-String für proportionales Rendering mit expliziter Font-Größe.
     * Für variable Zeilenhöhen: verschiedene heightQ → verschiedene Pixel-Größen.
     *
     * @param {boolean} isBold
     * @param {boolean} isItalic
     * @param {number} fontSize - Font-Größe in Pixel
     * @returns {string}
     */
    _getProportionalFontStringForSize(isBold, isItalic, fontSize) {
        return this._getFontStringForConfig(FONTS[this.currentFont], isBold, isItalic, fontSize)
    }

    /**
     * Erzeugt den Font-String fuer eine bestimmte Font-Config.
     * Wird von _getProportionalFontStringForSize und _createProportionalGlyph
     * (mit System-Font) verwendet.
     */
    _getFontStringForConfig(fontConfig, isBold, isItalic, fontSize) {
        let family
        if (isItalic) {
            family = isBold
                ? (fontConfig.familyBoldItalic || fontConfig.familyBold)
                : (fontConfig.familyItalic || fontConfig.family)
        } else {
            family = isBold ? fontConfig.familyBold : fontConfig.family
        }
        const weight = isBold ? 'bold ' : ''
        return `${weight}${fontSize}px ${family}, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"`
    }

    /**
     * Setzt den Canvas-Font nur wenn er sich geändert hat.
     * @param {boolean} isBold
     */
    _setFont(isBold) {
        const font = this._getFontString(isBold)
        if (font !== this._lastFont) {
            this.ctx.font = font
            this._lastFont = font
        }
    }

    /**
     * Setzt shadowBlur nur wenn er sich geändert hat.
     * @param {number} blur
     */
    _setShadowBlur(blur) {
        if (blur !== this._lastShadowBlur) {
            this.ctx.shadowBlur = blur
            this._lastShadowBlur = blur
        }
    }

    /**
     * Holt ein vorgerendertes Glyph aus dem Atlas oder erstellt es.
     *
     * @param {string} ch - Das Zeichen
     * @param {string} color - Vordergrundfarbe
     * @param {boolean} isBold
     * @param {boolean} isItalic
     * @param {boolean} hasGlow
     * @returns {OffscreenCanvas}
     */
    _getGlyph(ch, color, isBold, isItalic, hasGlow) {
        const key = `${ch}|${color}|${isBold ? 1 : 0}|${isItalic ? 1 : 0}|${hasGlow ? 1 : 0}`
        let glyph = this._glyphAtlas.get(key)
        if (!glyph) {
            glyph = this._createGlyph(ch, color, isBold, isItalic, hasGlow)
            this._glyphAtlas.set(key, glyph)
        }
        return glyph
    }

    /**
     * Erstellt ein vorgerendertes Glyph auf einem OffscreenCanvas.
     */
    _createGlyph(ch, color, isBold, isItalic, hasGlow) {
        const dpr = window.devicePixelRatio || 1
        const topPad = this._glyphTopPad
        const isWide = charDisplayWidth(ch) === 2  // Emoji = 2 Spalten
        const logicalW = isWide ? 2 * this.charWidth : this.charWidth
        const w = Math.ceil(logicalW * dpr)
        const h = Math.ceil((this.charHeight + topPad) * dpr)

        const canvas = new OffscreenCanvas(w, h)
        const ctx = canvas.getContext('2d')
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

        ctx.font = this._getFontString(isBold)
        ctx.fillStyle = color
        ctx.textBaseline = 'alphabetic'

        if (hasGlow && this.colors.glow.color) {
            ctx.shadowColor = this.colors.glow.color
            ctx.shadowBlur = this.colors.glow.blur
        }

        // Baseline um topPad nach unten verschoben — Raum für Diakritika oberhalb
        const baselineY = topPad + this._verticalPad + this._gridFontSize * 0.85
        if (isItalic) {
            ctx.save()
            ctx.transform(1, 0, -0.2, 1, 0, 0)
            const shearOffset = baselineY * 0.2
            ctx.fillText(ch, shearOffset, baselineY)
            ctx.restore()
        } else {
            ctx.fillText(ch, 0, baselineY)
        }

        return canvas
    }

    /**
     * Leert beide Glyph-Atlanten (bei Font/Size/Mode-Wechsel).
     */
    _invalidateGlyphAtlas() {
        this._glyphAtlas.clear()
        this._propGlyphAtlas.clear()
    }

    /**
     * Holt ein proportionales Glyph aus dem Atlas oder erstellt es.
     * Separate Map von Grid-Glyphs, da variable Canvas-Breite und nativer Italic.
     */
    _getProportionalGlyph(ch, color, isBold, isItalic, hasGlow, heightQ = 4, mono = false) {
        const key = `${ch}|${color}|${isBold ? 1 : 0}|${isItalic ? 1 : 0}|${hasGlow ? 1 : 0}|${heightQ}|${mono ? 1 : 0}`
        let glyph = this._propGlyphAtlas.get(key)
        if (!glyph) {
            glyph = this._createProportionalGlyph(ch, color, isBold, isItalic, hasGlow, heightQ, mono)
            this._propGlyphAtlas.set(key, glyph)
        }
        return glyph
    }

    /**
     * Erstellt ein proportionales Glyph mit nativer Italic-Familie und
     * metriken-basierter Canvas-Breite.
     */
    _createProportionalGlyph(ch, color, isBold, isItalic, hasGlow, heightQ = 4, mono = false) {
        const dpr = window.devicePixelRatio || 1
        const { cache: activeCache, scaleFactor: activeScale } = this._activeMetrics(heightQ)
        const localScaleFactor = activeScale * heightQ / 4
        const localCharHeight = this.charHeight * heightQ / 4
        const localFontSize = this._gridFontSize * heightQ / 4
        const localCharWidth = this.charWidth * heightQ / 4
        const localTopPad = this._glyphTopPad * heightQ / 4
        const localVerticalPad = this._verticalPad * heightQ / 4

        // Font-Config bestimmen (System-Font bei 4QL + Garamond)
        const { fontConfig: activeFontConfig } = this._activeMetrics(heightQ)
        const fontString = mono
            ? this._getGridFontStringForSize(isBold, localFontSize)
            : this._getFontStringForConfig(activeFontConfig, isBold, isItalic, localFontSize)

        // Breite bestimmen: Metriken-basiert (Emoji-Korrektur erfolgt in GlyphMetrics)
        let logicalWidth
        if (mono) {
            logicalWidth = localCharWidth
        } else {
            const m = activeCache?.get(ch, isBold, isItalic)
            logicalWidth = m ? m.visualRight * localScaleFactor + 2 : localCharWidth
        }
        const w = Math.max(1, Math.ceil(logicalWidth * dpr))
        const h = Math.ceil((localCharHeight + localTopPad) * dpr)

        const canvas = new OffscreenCanvas(w, h)
        const ctx = canvas.getContext('2d')
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

        ctx.font = fontString
        ctx.fillStyle = color
        ctx.textBaseline = 'alphabetic'

        if (hasGlow && this.colors.glow.color) {
            ctx.shadowColor = this.colors.glow.color
            ctx.shadowBlur = this.colors.glow.blur
        }

        // Baseline um localTopPad + localVerticalPad nach unten verschoben.
        // baselineFactor per Font konfigurierbar (Default 0.85) — EB Garamond
        // hat z.B. eine andere Oberlängen-Verteilung als Noto Sans und braucht
        // einen kleineren Wert, damit die 6QL-Glyphen nicht zu tief sitzen.
        const baselineFactor = (activeFontConfig && activeFontConfig.baselineFactor) || 0.85
        const baselineY = localTopPad + localVerticalPad + localFontSize * baselineFactor
        ctx.fillText(ch, 0, baselineY)

        return canvas
    }

    /**
     * Berechnet wie viele Zeichen bei aktueller Font-Größe auf den Canvas passen.
     *
     * @returns {{cols: number, rows: number}}
     */
    calculateGridDimensions() {
        const width = this._viewportWidth()
        const height = this._viewportHeight()

        const cols = Math.floor(width / this.charWidth)
        const rows = Math.floor(height / this.charHeight)

        return { cols, rows }
    }

    /**
     * Rendert den kompletten Screen auf den Canvas (ohne Cursor).
     * Der Cursor wird separat auf dem Cursor-Layer gerendert.
     * Overlay wird separat via renderOverlay() gerendert.
     *
     * @param {Screen} screen - Das Screen-Objekt mit dem Grid
     * @param {Cursor} cursor - Der Cursor (wird für Legacy-Grid-Rendering gebraucht)
     * @param {Overlay} overlay - Optionaler Overlay-Layer (nur für Cursor-Logik, nicht gerendert)
     * @param {LayoutManager} layout - Optionaler Layout-Manager
     */
    render(screen, cursor, overlay = null, layout = null) {
        // Canvas leeren: bei Layout nur die innerBounds (Border bleibt persistent
        // auf Main und wird nur bei _borderDirty neu gezeichnet). Ohne Layout
        // ein Full-Clear für das alte Grid-Rendering.
        if (layout) {
            this._clearInnerBounds(layout)
        } else {
            this._clear()
        }

        // Font/Shadow-Caches für neuen Render-Zyklus zurücksetzen
        this._lastFont = null
        this._lastShadowBlur = undefined

        // Layout-Rahmen zeichnen (falls vorhanden)
        if (layout) {
            this._renderLayout(layout)
        } else {
            // Ohne Layout: altes Verhalten (Grid mit Dummy-Content)
            this._renderGrid(screen, cursor, overlay)
        }

        // Overlay wird NICHT hier gerendert - main.js macht das separat nach dem Editor
    }

    /**
     * Wischt nur die innerBounds-Regionen aller Panes mit Background-Farbe.
     * Direct-Draw-Variante: der Border bleibt auf Main persistent stehen, wird
     * nur bei tatsächlicher Invalidierung (`_borderDirty`) neu gezeichnet.
     */
    _clearInnerBounds(layout) {
        this.ctx.fillStyle = this.colors.background
        const cw = this.charWidth
        const ch = this.charHeight
        for (const pane of Object.values(layout.panes)) {
            const ib = pane.innerBounds
            if (ib && ib.width > 0 && ib.height > 0) {
                this.ctx.fillRect(ib.x * cw, ib.y * ch, ib.width * cw, ib.height * ch)
            }
        }
    }

    /**
     * Rendert Pane-Rahmen, Tab-Bar und Uhr — aber nur bei `_borderDirty`.
     * Sonst bleibt der Border-Stand von vorherigen Frames auf Main stehen
     * (der Content wurde ja nur in den innerBounds gewischt).
     *
     * Direct-Draw ist einziger Pfad (Lesson 54). Der frühere Offscreen-Cache
     * triggerte beim Separator-Durchgang einen GPU-Compositor-Stall auf
     * Retina-4K.
     *
     * @param {LayoutManager} layout - Der Layout-Manager
     */
    _renderLayout(layout) {
        if (this._borderDirty) {
            this.renderBorderOnMain(layout)
            this._borderDirty = false
        }
    }

    /**
     * Zeichnet Pane-Borders, Tab-Bar und Uhr direkt aufs Hauptcanvas.
     *
     * Kein Clip nötig: Glow-Shadows bleiben innerhalb der Border-Cell, lecken
     * nicht in innerBounds. Der opaque Fill in den Zonen überschreibt den
     * alten Border-Stand auf Main — kein Alpha-Akkumulieren mehr, weil
     * Source-Over auf opaque ersetzt statt blendet.
     */
    renderBorderOnMain(layout) {
        this._fillBorderZonesOpaque(layout)

        this._lastFont = null
        this._lastShadowBlur = undefined

        for (const pane of Object.values(layout.panes)) {
            if (pane.bounds.width > 0 && pane.bounds.height > 0) {
                this._renderPane(pane, layout)
            }
        }

        if (layout.tabBarVisible && this._tabBarRenderer) {
            this._tabBarRenderer.render( this, this._tabManager,
                this._projectTitles, layout.cols,
                this._syncService
            )
        }

        this._renderClock(layout)

        // Shadow-State für nachfolgendes Pane-Content-Rendering zurücksetzen.
        this._setShadowBlur(0)
        this._lastFont = null
        this._lastShadowBlur = undefined
    }

    /**
     * Iteriert über alle Border-Zonen (Rahmen rings um jede Pane + Tab-Bar-Zeile)
     * und ruft `fn(x, y, w, h)` für jedes Rechteck in Pixel-Koordinaten auf.
     * Einheitliche Geometrie für Fill — damit `_fillBorderZonesOpaque` exakt
     * die Zonen trifft, in die `renderBorderOnMain` danach zeichnet.
     */
    _forEachBorderZone(layout, fn) {
        const cw = this.charWidth
        const ch = this.charHeight

        if (layout.tabBarVisible) {
            fn(0, 0, this._viewportWidth(), ch)
        }

        for (const pane of Object.values(layout.panes)) {
            const b = pane.bounds
            if (!b || b.width <= 0 || b.height <= 0) continue
            const ib = pane.innerBounds
            // Collapsed Pane oder keine innerBounds: kompletter bounds ist Rahmen
            if (!ib || ib.width <= 0 || ib.height <= 0) {
                fn(b.x * cw, b.y * ch, b.width * cw, b.height * ch)
                continue
            }
            const bx = b.x * cw, by = b.y * ch, bw = b.width * cw, bh = b.height * ch
            const ix = ib.x * cw, iy = ib.y * ch, iw = ib.width * cw, ih = ib.height * ch

            if (iy > by) fn(bx, by, bw, iy - by)                          // Top
            if (iy + ih < by + bh) fn(bx, iy + ih, bw, by + bh - iy - ih) // Bottom
            if (ix > bx) fn(bx, iy, ix - bx, ih)                          // Left
            if (ix + iw < bx + bw) fn(ix + iw, iy, bx + bw - ix - iw, ih) // Right
        }
    }

    /**
     * Füllt die Rahmen-Zonen des Hauptcanvas opaque mit der Background-Farbe.
     * Rahmen-Zone = bounds \ innerBounds + Tab-Bar-Zeile. InnerBounds bleiben
     * unberührt (dort steht der Content vom Scroll-Shift).
     *
     * Opaque-Fill vor dem Border-Rerender: alte Border-Pixel (Labels, Glow-Hof)
     * werden vollständig ersetzt, kein Alpha-Akkumulieren bei Label-Wechsel.
     */
    _fillBorderZonesOpaque(layout) {
        this.ctx.fillStyle = this.colors.background
        this._forEachBorderZone(layout, (x, y, w, h) => this.ctx.fillRect(x, y, w, h))
    }

    /**
     * Rendert die Uhr (hh:mm) an der festen Position unten rechts.
     * Wird immer zuletzt gerendert, damit sie über jedem Pane-Rahmen erscheint.
     */
    _renderClock(layout) {
        const now = new Date()
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        const bottomY = layout.rows - 1
        const clockX = layout.cols - 2 - timeStr.length

        // Bereich freimachen (Box-Drawing-Linien übermalen)
        const pixelX = clockX * this.charWidth
        const pixelY = bottomY * this.charHeight
        this.ctx.fillStyle = this.colors.background
        this.ctx.fillRect(pixelX, pixelY, timeStr.length * this.charWidth, this.charHeight)

        for (let i = 0; i < timeStr.length; i++) {
            this._renderCharAt(clockX + i, bottomY, timeStr[i], Style.PANE_BORDER)
        }
    }

    /**
     * Rendert einen einzelnen Bereich (ohne Cursor - der ist auf dem Cursor-Layer).
     *
     * @param {Pane} pane - Der zu rendernde Bereich
     * @param {LayoutManager} layout - Der Layout-Manager (für Kontext)
     */
    _renderPane(pane, layout) {
        // Editor-Pane: aktiv wenn es der aktive Editor-Pane ist (immer, auch bei Console-Fokus)
        // Core-Pane: aktiv nur im FOCUSED-Zustand (nicht bei HOVER)
        const isActive = pane.id === PaneId.CORE
            ? layout.isConsoleFocused()
            : layout.getActiveEditorPane() === pane.id

        // Für collapsed-Bereiche: nur vertikale/horizontale Linie
        if (pane.state === PaneState.COLLAPSED) {
            this._renderCollapsedPane(pane, layout)
            return
        }

        // Border-Linien als durchgehender Canvas-Path (Lesson 54): statt pro
        // Grid-Zelle einzelne Box-Char-Strokes. Reduziert Stroke-Aufrufe
        // drastisch und löst den GPU-Compositor-Stall am Separator-Durchgang.
        const borderStyle = isActive ? Style.PANE_BORDER_ACTIVE : Style.PANE_BORDER
        const topGaps = []
        const bottomGaps = []
        this._renderTopBorderContent(pane, layout, isActive, borderStyle, topGaps)
        this._renderBottomBorderContent(pane, layout, borderStyle, bottomGaps)
        this._strokePaneBorderLines(pane, borderStyle, topGaps, bottomGaps)
    }

    /**
     * Rendert Controls und Label im Top-Border. Füllt topGaps mit Pixel-X-
     * Ranges der Aussparungen. Die horizontalen Linien selbst werden hier
     * NICHT gezeichnet — die kommen aus dem durchgehenden Path.
     */
    _renderTopBorderContent(pane, layout, isActive, borderStyle, topGaps) {
        const b = pane.bounds
        const titleStyle = isActive ? Style.PANE_TITLE_ACTIVE : Style.PANE_TITLE
        const isFullscreen = pane.state === PaneState.FULLSCREEN
        const cw = this.charWidth
        const isProp = this.isProportional()

        // Sprung über: [0]=TOP_LEFT, [1]=HORIZONTAL (Spacer vor Controls)
        let x = b.x + 2

        // (+) / (+|-) / (-) Control(s)
        if (isProp) {
            if (isFullscreen) {
                const minusStyle = this._getControlStyle(pane.id, ControlType.COLLAPSE, layout)
                this._renderControlIcon(x + 1, b.y, '-', minusStyle, borderStyle)
                topGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
            } else {
                const plusStyle = this._getControlStyle(pane.id, ControlType.EXPAND, layout)
                this._renderControlIcon(x + 1, b.y, '+', plusStyle, borderStyle)
                const gapStartX = x
                x += 3
                if (layout.hasCollapseControl(pane.id)) {
                    // Compound: zweiter Kreis berührt den ersten (Zentren 2 Zellen Abstand)
                    x -= 1  // Überlappung: neues Center liegt 2 Zellen hinter dem ersten
                    const minusStyle = this._getControlStyle(pane.id, ControlType.COLLAPSE, layout)
                    this._renderControlIcon(x + 1, b.y, '-', minusStyle, borderStyle)
                    x += 3
                }
                topGaps.push({ start: gapStartX * cw, end: x * cw })
            }
        } else {
            // Monospace: "(+)" / "(+|-)" / "(-)" als Text-Chars
            this._renderCharAt(x, b.y, '(', borderStyle)
            if (isFullscreen) {
                const minusStyle = this._getControlStyle(pane.id, ControlType.COLLAPSE, layout)
                this._renderCharAt(x + 1, b.y, '-', minusStyle)
                this._renderCharAt(x + 2, b.y, ')', borderStyle)
                topGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
            } else {
                const plusStyle = this._getControlStyle(pane.id, ControlType.EXPAND, layout)
                this._renderCharAt(x + 1, b.y, '+', plusStyle)
                if (layout.hasCollapseControl(pane.id)) {
                    this._renderCharAt(x + 2, b.y, '|', borderStyle)
                    const minusStyle = this._getControlStyle(pane.id, ControlType.COLLAPSE, layout)
                    this._renderCharAt(x + 3, b.y, '-', minusStyle)
                    this._renderCharAt(x + 4, b.y, ')', borderStyle)
                    topGaps.push({ start: x * cw, end: (x + 5) * cw })
                    x += 5
                } else {
                    this._renderCharAt(x + 2, b.y, ')', borderStyle)
                    topGaps.push({ start: x * cw, end: (x + 3) * cw })
                    x += 3
                }
            }
        }

        // Spacer-Zelle nach Controls (eine HORIZONTAL-Breite) — Linie läuft durch
        x += 1

        // Label (Titel)
        const title = pane.title
        const labelStartX = x
        const maxTitleCols = b.x + b.width - 2 - x
        if (title.length > 0 && maxTitleCols > 0) {
            const metrics = isProp ? {} : null
            x = this.renderLabel(title, x, b.y, titleStyle, isActive, maxTitleCols, metrics)
            if (x > labelStartX) {
                if (metrics) {
                    // Prop: exakte Pixel-Range + kleines Padding, sonst entstehen
                    // ungleichmaessige Lucken durch Cell-Aufrundung.
                    const padding = cw / 4
                    topGaps.push({
                        start: labelStartX * cw - padding,
                        end: metrics.endPx + padding
                    })
                } else {
                    topGaps.push({ start: labelStartX * cw, end: x * cw })
                }
            }
        }

        // Spacer-Zelle nach Label
        x += 1

        // Rechte Controls: Separator (Outline) + Lock (Main)
        const sepControl = layout.hasSeparatorControl(pane.id) ? layout.getSeparatorControl(pane.id) : null
        const lockControl = layout.hasLockControl(pane.id) ? layout.getLockControl(pane.id) : null

        while (x < b.x + b.width - 1) {
            if (sepControl && x === sepControl.x - 1) {
                const sepStyle = this._getControlStyle(pane.id, ControlType.SEPARATOR, layout)
                if (isProp) {
                    this._renderControlIcon(sepControl.x, b.y, '=', sepStyle, borderStyle)
                } else {
                    this._renderCharAt(x, b.y, '(', borderStyle)
                    this._renderCharAt(x + 1, b.y, '=', sepStyle)
                    this._renderCharAt(x + 2, b.y, ')', borderStyle)
                }
                topGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
                continue
            }
            if (lockControl && x === lockControl.x - 1) {
                const lockStyle = this._getControlStyle(pane.id, ControlType.LOCK, layout)
                const lockChar = layout.isCurrentSectionLocked ? 'v' : ' '
                if (isProp) {
                    this._renderControlIcon(lockControl.x, b.y, lockChar, lockStyle, borderStyle)
                } else {
                    this._renderCharAt(x, b.y, '(', borderStyle)
                    this._renderCharAt(x + 1, b.y, lockChar, lockStyle)
                    this._renderCharAt(x + 2, b.y, ')', borderStyle)
                }
                topGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
                continue
            }
            x += 1
        }
    }

    /**
     * Rendert Controls im Bottom-Border (Dictation + Background). Füllt
     * bottomGaps mit den Aussparungs-Ranges.
     */
    _renderBottomBorderContent(pane, layout, borderStyle, bottomGaps) {
        const b = pane.bounds
        const bottomY = b.y + b.height - 1
        const cw = this.charWidth
        const isProp = this.isProportional()

        const dictControl = layout.hasDictationControl(pane.id) ? layout.getDictationControl(pane.id) : null
        const abortControl = layout.hasAbortControl(pane.id) ? layout.getAbortControl(pane.id) : null
        const bgControl = layout.hasBackgroundControl(pane.id) ? layout.getBackgroundControl(pane.id) : null
        const syncWarnControl = layout.hasSyncWarningControl?.(pane.id) ? layout.getSyncWarningControl(pane.id) : null

        let x = b.x + 1  // skip BOTTOM_LEFT Ecke
        while (x < b.x + b.width - 1) {
            if (abortControl && x === abortControl.x - 1) {
                const abortStyle = this._getAbortControlStyle(pane.id, layout)
                if (isProp) {
                    this._renderControlIcon(abortControl.x, bottomY, '!', abortStyle, borderStyle)
                } else {
                    this._renderCharAt(x, bottomY, '(', borderStyle)
                    this._renderCharAt(x + 1, bottomY, '!', abortStyle)
                    this._renderCharAt(x + 2, bottomY, ')', borderStyle)
                }
                bottomGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
                continue
            }
            if (dictControl && x === dictControl.x - 1) {
                const dictStyle = this._getDictationControlStyle(pane.id, layout)
                const dictChar = (layout.isDictationActive || layout.isDictationStarting) ? '●' : 'o'
                if (isProp) {
                    this._renderControlIcon(dictControl.x, bottomY, dictChar, dictStyle, borderStyle)
                } else {
                    this._renderCharAt(x, bottomY, '(', borderStyle)
                    this._renderCharAt(x + 1, bottomY, dictChar, dictStyle)
                    this._renderCharAt(x + 2, bottomY, ')', borderStyle)
                }
                bottomGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
                continue
            }
            if (syncWarnControl && x === syncWarnControl.x - 1) {
                const warnStyle = this._getSyncWarningStyle(pane.id, layout)
                if (isProp) {
                    this._renderControlIcon(syncWarnControl.x, bottomY, '!', warnStyle, borderStyle)
                } else {
                    this._renderCharAt(x, bottomY, '(', borderStyle)
                    this._renderCharAt(x + 1, bottomY, '!', warnStyle)
                    this._renderCharAt(x + 2, bottomY, ')', borderStyle)
                }
                bottomGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
                continue
            }
            if (bgControl && x === bgControl.x - 1) {
                const bgStyle = this._getControlStyle(pane.id, ControlType.BACKGROUND, layout)
                if (isProp) {
                    this._renderControlIcon(bgControl.x, bottomY, '=', bgStyle, borderStyle)
                } else {
                    this._renderCharAt(x, bottomY, '(', borderStyle)
                    this._renderCharAt(x + 1, bottomY, '=', bgStyle)
                    this._renderCharAt(x + 2, bottomY, ')', borderStyle)
                }
                bottomGaps.push({ start: x * cw, end: (x + 3) * cw })
                x += 3
                continue
            }
            x += 1
        }
    }

    /**
     * Strokt die vier Border-Linien (Top/Bottom/Left/Right) in EINEM Path —
     * pixel-scharf. Vertikale-X und Horizontale-Y auf Pixel-Center gesnappt
     * (`Math.floor + 0.5`), Endpunkte auf integer. `lineCap='butt'` mit
     * pixelgenauen Endpunkten ergibt saubere 90°-Ecken ohne Überlappung.
     */
    _strokePaneBorderLines(pane, borderStyle, topGaps, bottomGaps) {
        const b = pane.bounds
        const cw = this.charWidth
        const ch = this.charHeight

        // Koordinaten analog _drawBoxLines: Zellenmitte-X für Vertikale,
        // fontzentrierte Y für Horizontale — direkt auf Pixel-Center gesnappt
        const leftColCx = Math.floor(b.x * cw + cw / 2) + 0.5
        const rightColCx = Math.floor((b.x + b.width - 1) * cw + cw / 2) + 0.5
        const topRowCy = Math.floor(b.y * ch + this._verticalPad + this._gridFontSize / 2) + 0.5
        const bottomRowCy = Math.floor((b.y + b.height - 1) * ch + this._verticalPad + this._gridFontSize / 2) + 0.5

        const { color } = this._resolveStyle(borderStyle)
        const scale = Math.max(1, this._scaleFactor)

        this.ctx.strokeStyle = color
        this.ctx.lineWidth = 1 * scale
        this.ctx.lineCap = 'butt'
        this._setShadowBlur(0)

        this.ctx.beginPath()

        // Linke Vertikale
        this.ctx.moveTo(leftColCx, topRowCy)
        this.ctx.lineTo(leftColCx, bottomRowCy)

        // Rechte Vertikale
        this.ctx.moveTo(rightColCx, topRowCy)
        this.ctx.lineTo(rightColCx, bottomRowCy)

        // Top-Horizontale mit Aussparungen
        this._addHorizontalSegmentsToPath(leftColCx, rightColCx, topRowCy, topGaps)

        // Bottom-Horizontale mit Aussparungen
        this._addHorizontalSegmentsToPath(leftColCx, rightColCx, bottomRowCy, bottomGaps)

        this.ctx.stroke()
    }

    /**
     * Fügt Horizontal-Linien-Segmente zwischen startX und endX an Y=y zum
     * aktuellen Canvas-Path hinzu. Gap-Grenzen werden auf integer gerundet
     * (Endkappen auf Pixel-Grid); y wird unverändert übernommen (Aufrufer
     * hat bereits auf Pixel-Center gesnappt).
     */
    _addHorizontalSegmentsToPath(startX, endX, y, gaps) {
        const sorted = gaps.slice().sort((a, b) => a.start - b.start)
        let cursor = startX
        for (const gap of sorted) {
            const gapStart = Math.round(gap.start)
            const gapEnd = Math.round(gap.end)
            if (cursor < gapStart) {
                this.ctx.moveTo(Math.round(cursor), y)
                this.ctx.lineTo(gapStart, y)
            }
            if (gapEnd > cursor) cursor = gapEnd
        }
        if (cursor < endX) {
            this.ctx.moveTo(Math.round(cursor), y)
            this.ctx.lineTo(Math.round(endX), y)
        }
    }

    /**
     * Ermittelt den Style für ein Control.
     *
     * @param {string} paneId
     * @param {string} controlType
     * @param {LayoutManager} layout
    /**
     * Spezielle Style-Logik für das Diktat-Control (o) / (●).
     * - Diktat startet: CONTROL_STARTING (Orange)
     * - Diktat aktiv: CONTROL_HIGHLIGHT (Grün)
     * - Hover: CONTROL_HOVER
     * - Sonst: CONTROL_NORMAL
     */
    _getDictationControlStyle(paneId, layout) {
        // Diktat startet → Orange (auf allen Panes)
        if (layout.isDictationStarting) {
            return Style.CONTROL_STARTING
        }

        // Diktat aktiv → leuchtendes Grün (auf allen Panes)
        if (layout.isDictationActive) {
            return Style.CONTROL_DICTATION_ACTIVE
        }

        // Standard-Logik (Navigation, Hover, Normal)
        return this._getControlStyle(paneId, ControlType.DICTATION, layout)
    }

    /**
     * Spezielle Style-Logik für das Abort-Control (!).
     * - Hover oder Nav-Highlight: übliche Highlight/Hover-Styles
     * - Sonst: CONTROL_ABORT (Rot) — das Control ist nur sichtbar während
     *   der Agent arbeitet, die rote Farbe signalisiert "Notaus".
     */
    _getAbortControlStyle(paneId, layout) {
        if (layout.navigationMode) {
            const current = layout.getCurrentControl()
            if (current && current.paneId === paneId && current.type === ControlType.ABORT) {
                return Style.CONTROL_HIGHLIGHT
            }
        }
        if (layout.hoveredControl &&
            layout.hoveredControl.paneId === paneId &&
            layout.hoveredControl.type === ControlType.ABORT) {
            return Style.CONTROL_HOVER
        }
        return Style.CONTROL_ABORT
    }

    /**
     * Style fuer das Sync-Warn-Control (!): Orange als Dauerzustand
     * ("Cloud-Sync gestoert"), Hover ueber die uebliche Hover-Farbe.
     */
    _getSyncWarningStyle(paneId, layout) {
        if (layout.hoveredControl &&
            layout.hoveredControl.paneId === paneId &&
            layout.hoveredControl.type === ControlType.SYNC_WARNING) {
            return Style.CONTROL_HOVER
        }
        return Style.SYNC_PENDING
    }

    _getControlStyle(paneId, controlType, layout) {
        // Im Navigations-Modus: aktuelles Control highlighten
        if (layout.navigationMode) {
            const current = layout.getCurrentControl()
            if (current && current.paneId === paneId && current.type === controlType) {
                return Style.CONTROL_HIGHLIGHT
            }
        }

        // Hover-State
        if (layout.hoveredControl &&
            layout.hoveredControl.paneId === paneId &&
            layout.hoveredControl.type === controlType) {
            return Style.CONTROL_HOVER
        }

        return Style.CONTROL_NORMAL
    }

    /**
     * Rendert einen eingeklappten Bereich (vertikale/horizontale Linie mit +).
     * Linien als Path (wie normale Panes), Controls/Label unverändert.
     * Cursor wird separat auf dem Cursor-Layer gerendert.
     *
     * @param {Pane} pane
     * @param {LayoutManager} layout
     */
    _renderCollapsedPane(pane, layout) {
        const b = pane.bounds
        const style = Style.PANE_BORDER
        const plusStyle = this._getControlStyle(pane.id, ControlType.EXPAND, layout)
        const cw = this.charWidth
        const ch = this.charHeight

        if (pane.id === PaneId.OUTLINE || pane.id === PaneId.MAIN || pane.id === PaneId.CONTEXT) {
            // + oben (Atlas-Glyph, kein Icon)
            this._renderCharAt(b.x, b.y, '+', plusStyle)

            // Vertikale Linie von unter dem + bis zum unteren Rand (exklusive)
            const cx = b.x * cw + cw / 2
            const yStart = (b.y + 1) * ch
            const yEnd = (b.y + b.height - 1) * ch
            if (yEnd > yStart) {
                this._strokeLinePath(style, [[cx, yStart, cx, yEnd]])
            }
        } else if (pane.id === PaneId.CORE) {
            // Horizontale Linie mit (+) und Name — Format: ─(+)─Core────
            // Aussparungen: (+) bei Zellen [b.x+2..b.x+4], Label ab b.x+6
            const gaps = []

            // (+) Control
            if (this.isProportional()) {
                this._renderControlIcon(b.x + 3, b.y, '+', plusStyle, style)
            } else {
                this._renderCharAt(b.x + 2, b.y, '(', style)
                this._renderCharAt(b.x + 3, b.y, '+', plusStyle)
                this._renderCharAt(b.x + 4, b.y, ')', style)
            }
            gaps.push({ start: (b.x + 2) * cw, end: (b.x + 5) * cw })

            // Name "Core"
            const labelStartX = b.x + 6
            const maxTitleCols = b.x + b.width - 1 - labelStartX
            const metrics = this.isProportional() ? {} : null
            const labelEndX = this.renderLabel('Core', labelStartX, b.y, Style.PANE_TITLE, false, maxTitleCols, metrics)
            if (labelEndX > labelStartX) {
                if (metrics) {
                    const padding = cw / 4
                    gaps.push({
                        start: labelStartX * cw - padding,
                        end: metrics.endPx + padding
                    })
                } else {
                    gaps.push({ start: labelStartX * cw, end: labelEndX * cw })
                }
            }

            // Horizontale Linie von b.x+1 bis b.x+b.width-1, mit Aussparungen.
            // Der klassische Code startete die Linie an Zelle b.x+1 (linker Rand
            // = (b.x+1)*cw) und ließ rechts eine Zelle frei (endet bei
            // (b.x+b.width-1)*cw). cy wird hier auf Pixel-Center gesnappt,
            // damit die Linie nicht per Anti-Aliasing auf zwei Pixel läuft —
            // Invariante der `_addHorizontalSegmentsToPath`-Aufrufer.
            const cy = Math.floor(b.y * ch + this._verticalPad + this._gridFontSize / 2) + 0.5
            const startX = (b.x + 1) * cw
            const endX = (b.x + b.width - 1) * cw
            this._strokeHorizontalWithGaps(style, startX, endX, cy, gaps)
        }
    }

    /**
     * Strokt eine Liste einfacher Linien als EINEN Path — pixel-scharf.
     * Bei rein vertikalen (x0==x1) oder rein horizontalen (y0==y1) Segmenten
     * wird die Haupt-Achse auf Pixel-Center (`Math.floor + 0.5`) gesnappt,
     * die Endpunkte auf integer — butt-Cap schließt dann nahtlos an.
     * segments: Array von [x0, y0, x1, y1].
     */
    _strokeLinePath(borderStyle, segments) {
        const { color } = this._resolveStyle(borderStyle)
        const scale = Math.max(1, this._scaleFactor)

        this.ctx.strokeStyle = color
        this.ctx.lineWidth = 1 * scale
        this.ctx.lineCap = 'butt'
        this._setShadowBlur(0)

        this.ctx.beginPath()
        for (const [x0, y0, x1, y1] of segments) {
            if (x0 === x1) {
                // vertikal: x auf Pixel-Center, y auf integer
                const cx = Math.floor(x0) + 0.5
                this.ctx.moveTo(cx, Math.round(y0))
                this.ctx.lineTo(cx, Math.round(y1))
            } else if (y0 === y1) {
                // horizontal: y auf Pixel-Center, x auf integer
                const cy = Math.floor(y0) + 0.5
                this.ctx.moveTo(Math.round(x0), cy)
                this.ctx.lineTo(Math.round(x1), cy)
            } else {
                this.ctx.moveTo(x0, y0)
                this.ctx.lineTo(x1, y1)
            }
        }
        this.ctx.stroke()
    }

    /**
     * Strokt eine horizontale Linie mit Aussparungen als EINEN Path — pixel-scharf.
     */
    _strokeHorizontalWithGaps(borderStyle, startX, endX, y, gaps) {
        const { color } = this._resolveStyle(borderStyle)
        const scale = Math.max(1, this._scaleFactor)

        this.ctx.strokeStyle = color
        this.ctx.lineWidth = 1 * scale
        this.ctx.lineCap = 'butt'
        this._setShadowBlur(0)

        this.ctx.beginPath()
        this._addHorizontalSegmentsToPath(startX, endX, y, gaps)
        this.ctx.stroke()
    }

    /**
     * Rendert den Cursor an einer Position auf dem Cursor-Layer (blinkender Block).
     *
     * @param {number} charX - Spalte
     * @param {number} charY - Zeile
     */
    _renderCursorAt(charX, charY) {
        const ctx = this._cursorCtx || this.ctx
        const x = charX * this.charWidth
        const y = charY * this.charHeight

        ctx.fillStyle = this.colors.cursor
        if (this.colors.glow.color) {
            ctx.shadowColor = this.colors.glow.color
            ctx.shadowBlur = this.colors.glow.blur
        }
        ctx.fillRect(x, y, this.charWidth, this.charHeight)
        if (this.colors.glow.color) {
            ctx.shadowBlur = 0
        }
    }

    /**
     * Rendert einen I-Beam-Cursor an einer Pixel-Position.
     * Vertikaler Strich, Breite skaliert mit Font-Größe.
     */
    _renderIBeamCursorAt(pixelX, charY, clipTop = -Infinity, clipBottom = Infinity, heightQ = 4, pixelYOffset = 0) {
        const ctx = this._cursorCtx || this.ctx
        // Proportional: Cursor vertikal zentriert in der Zelle (wie der Text)
        // pixelYOffset: Baseline-Shift für Note/Info unter Heading-Zeilen
        const vertOffset = this.isProportional() ? this._verticalPad * heightQ / 4 : 0
        const y = charY * this.charHeight + vertOffset + pixelYOffset
        // Cursor-Höhe passend zur Zeilenhöhe (heightQ)
        const cursorH = this.isProportional() ? this.textHeightForHeightQ(heightQ) : this.charHeight

        // Clipping: Cursor an Pane-Grenzen beschneiden
        let drawY = y
        let drawH = cursorH
        if (drawY < clipTop) {
            drawH -= (clipTop - drawY)
            drawY = clipTop
        }
        if (drawY + drawH > clipBottom) {
            drawH = clipBottom - drawY
        }
        if (drawH <= 0) return

        ctx.fillStyle = this.colors.cursor
        if (this.colors.glow.color) {
            ctx.shadowColor = this.colors.glow.color
            ctx.shadowBlur = this.colors.glow.blur
        }

        const ibeamWidth = this._getScaledStrokeWidth(0.10, 2)
        const halfWidth = Math.floor(ibeamWidth / 2)
        ctx.fillRect(pixelX - halfWidth, drawY, ibeamWidth, drawH)

        if (this.colors.glow.color) {
            ctx.shadowBlur = 0
        }
    }

    /**
     * Rendert den Cursor-Layer (separates Canvas, nur Cursor).
     * Wird bei jedem Blink und nach jedem Content-Render aufgerufen.
     * Sehr billig: 1x clearRect + max. 1x fillRect.
     *
     * @param {Cursor} cursor - Der Cursor
     * @param {LayoutManager} layout - Der Layout-Manager
     * @param {boolean} showCursor - false wenn Overlay/Menü aktiv
     */
    renderCursorLayer(cursor, layout, showCursor = true) {
        if (!this._cursorCtx) return

        // Cursor-Canvas komplett leeren (transparent) — über Identity-Transform,
        // damit auch der Grid-Zentrierungs-Offset-Streifen mit gecleart wird.
        this._cursorCtx.save()
        this._cursorCtx.setTransform(1, 0, 0, 1, 0, 0)
        this._cursorCtx.clearRect(0, 0, this._cursorCanvas.width, this._cursorCanvas.height)
        this._cursorCtx.restore()

        // Kein Cursor wenn unsichtbar oder Overlay aktiv
        if (!showCursor || !cursor.visible) return

        const currentStation = layout.getCurrentStation()

        // Cursor auf Control (+/- Buttons): immer Block-Cursor
        if (currentStation && currentStation.isControl()) {
            this._renderCursorAt(currentStation.x, currentStation.y)
            return
        }

        // 1. Editor-Cursor IMMER rendern (blinkt immer)
        const editorPane = layout.panes[layout.getActiveEditorPane()]
        if (editorPane && editorPane.state !== PaneState.COLLAPSED) {
            const onEditorStation = !currentStation ||
                (currentStation.paneId === editorPane.id && currentStation.isCursor())
            if (onEditorStation) {
                this._renderPaneCursor(editorPane)
            }
        }

        // 2. Core-Cursor nur bei HOVER oder FOCUSED
        if (layout.isConsoleActive()) {
            const corePane = layout.panes[PaneId.CORE]
            if (corePane && corePane.state !== PaneState.COLLAPSED) {
                this._renderPaneCursor(corePane)
            }
        }
    }

    /**
     * Rendert den Cursor in einem Pane (Editor oder Core).
     * @param {Object} pane - Pane mit cursorPosition und innerBounds
     */
    _renderPaneCursor(pane) {
        if (pane.cursorPosition.x < 0 || pane.cursorPosition.y < 0) return

        const cursorX = pane.innerBounds.x + pane.cursorPosition.x
        const cursorY = pane.innerBounds.y + pane.cursorPosition.y
        const ib = pane.innerBounds
        const heightQ = pane.cursorPosition.heightQ || 4

        if (this.isProportional() && pane.cursorPosition.pixelX !== undefined) {
            const pixelX = ib.x * this.charWidth + pane.cursorPosition.pixelX
            const pixelXMax = (ib.x + ib.width) * this.charWidth
            // pixelYOffset: Baseline-Shift für Note/Info unter Heading-Zeilen
            const pixelYOffset = pane.cursorPosition.pixelYOffset || 0
            const vertOffset = this._verticalPad * heightQ / 4
            const cursorPxY = cursorY * this.charHeight + vertOffset + pixelYOffset
            const cursorH = this.textHeightForHeightQ(heightQ)
            const clipTop = ib.y * this.charHeight
            const clipBottom = (ib.y + ib.height) * this.charHeight
            const partiallyVisible = cursorPxY + cursorH > clipTop && cursorPxY < clipBottom
            if (pixelX >= ib.x * this.charWidth && pixelX < pixelXMax && partiallyVisible) {
                this._renderIBeamCursorAt(pixelX, cursorY, clipTop, clipBottom, heightQ, pixelYOffset)
            }
        } else {
            const cursorFullyVisible = cursorY >= ib.y && cursorY + 1 <= ib.y + ib.height
            if (cursorX >= ib.x && cursorX < ib.x + ib.width && cursorFullyVisible) {
                this._renderCursorAt(cursorX, cursorY)
            }
        }
    }

    /**
     * Rendert ein einzelnes Zeichen an einer Character-Position.
     *
     * @param {number} charX - Spalte
     * @param {number} charY - Zeile
     * @param {string} ch - Das Zeichen
     * @param {string} style - Style-Name
     */
    _renderCharAt(charX, charY, ch, style) {
        const x = charX * this.charWidth
        const y = charY * this.charHeight

        this._renderChar(x, y, { char: ch, style })
    }

    /**
     * Rendert ein Text-Label proportional (oder Monospace als Fallback).
     * Gibt die nächste Grid-Spalte nach dem Label zurück.
     *
     * @param {string} text - Der Text
     * @param {number} gridX - Start-Spalte
     * @param {number} gridY - Zeile
     * @param {string} style - Style-Name
     * @param {boolean} isBold - Fett rendern
     * @param {number} [maxGridCols] - Maximale Breite in Grid-Spalten (optional, clippt den Text)
     * @returns {number} Nächste freie Grid-Spalte
     */
    renderLabel(text, gridX, gridY, style, isBold = false, maxGridCols, outMetrics = null) {
        if (!text || text.length === 0) {
            if (outMetrics) outMetrics.endPx = gridX * this.charWidth
            return gridX
        }

        // Canvas-Clipping wenn Breitenbegrenzung gesetzt
        const hasClip = maxGridCols != null
        if (hasClip) {
            this.ctx.save()
            this.ctx.beginPath()
            this.ctx.rect(
                gridX * this.charWidth, gridY * this.charHeight,
                maxGridCols * this.charWidth, this.charHeight
            )
            this.ctx.clip()
        }

        let result
        let endPx
        const { cache: labelCache, scaleFactor: labelScale } = this._activeMetrics(4)
        if (this.isProportional() && labelCache) {
            const startPx = gridX * this.charWidth
            const formatRuns = [{
                start: 0, end: text.length,
                bold: isBold, italic: false, underline: false, strikethrough: false,
                style
            }]
            this.renderProportionalLine(
                this.ctx, text, startPx, gridY * this.charHeight, formatRuns, null, null, null
            )
            const layout = computeStringLayout(
                text, labelCache, labelCache.baseStep, formatRuns
            )
            const widthPx = layout.totalWidth * labelScale
            endPx = startPx + widthPx
            const endCol = Math.max(gridX + 1, Math.ceil(endPx / this.charWidth))
            result = hasClip ? Math.min(endCol, gridX + maxGridCols) : endCol
            if (hasClip) endPx = Math.min(endPx, (gridX + maxGridCols) * this.charWidth)
        } else {
            // Monospace-Fallback
            const maxX = hasClip ? gridX + maxGridCols : Infinity
            let x = gridX
            for (const ch of text) {
                if (x >= maxX) break
                this._renderCharAt(x, gridY, ch, style)
                x++
            }
            result = x
            endPx = result * this.charWidth
        }

        if (hasClip) {
            this.ctx.restore()
        }
        if (outMetrics) outMetrics.endPx = endPx
        return result
    }

    /**
     * Berechnet die Grid-Breite eines Text-Labels (proportional oder Monospace).
     *
     * @param {string} text - Der Text
     * @param {boolean} isBold - Fett
     * @returns {number} Breite in Grid-Spalten
     */
    labelGridWidth(text, isBold = false) {
        if (!text || text.length === 0) return 0

        const { cache: labelCache, scaleFactor: labelScale } = this._activeMetrics(4)
        if (this.isProportional() && labelCache) {
            const formatRuns = [{
                start: 0, end: text.length,
                bold: isBold, italic: false, underline: false, strikethrough: false,
                style: Style.EDITOR_TEXT
            }]
            const layout = computeStringLayout(
                text, labelCache, labelCache.baseStep, formatRuns
            )
            return Math.max(1, Math.ceil(layout.totalWidth * labelScale / this.charWidth))
        }

        return text.length
    }

    /**
     * Rendert ein einzelnes Zeichen mit optionalem Selection-Hintergrund
     * und optionalem Such-Highlighting.
     *
     * @param {number} charX - Spalte
     * @param {number} charY - Zeile
     * @param {string} ch - Das Zeichen
     * @param {string} style - Style-Name
     * @param {boolean} isSelected - Ist das Zeichen selektiert?
     * @param {string|null} highlightType - 'match' | 'current' | null (Such-Highlight)
     */
    _renderCharAtWithSelection(charX, charY, ch, style, isSelected, highlightType = null) {
        const x = charX * this.charWidth
        const y = charY * this.charHeight
        // Proportional: Selection/Highlight vertikal zentriert, nur so hoch wie die Buchstaben
        const hlY = this.isProportional() ? y + this._verticalPad : y
        const hlHeight = this.isProportional() ? this._textHeight : this.charHeight
        // Emoji belegt 2 Spalten im Monospace-Raster
        const hlW = charDisplayWidth(ch) === 2 ? 2 * this.charWidth : this.charWidth

        // Such-Highlight hat niedrigere Prioritaet als Selection
        const isCurrentMatch = !isSelected && highlightType === 'current'
        if (!isSelected && highlightType) {
            if (this.isNerdMode()) {
                // Nerd-Mode: nur aktuellen Match invertiert darstellen (wie Selection)
                if (isCurrentMatch) {
                    this.ctx.fillStyle = this._getHighlightBgColor(STYLES[Style.EDITOR_SELECTION])
                    this.ctx.fillRect(x, hlY, hlW, hlHeight)
                }
            } else {
                const hlStyle = highlightType === 'current'
                    ? STYLES[Style.SEARCH_CURRENT_MATCH]
                    : STYLES[Style.SEARCH_MATCH]

                if (hlStyle) {
                    const bgColor = this._getHighlightBgColor(hlStyle)
                    if (bgColor) {
                        this.ctx.fillStyle = bgColor
                        this.ctx.fillRect(x, hlY, hlW, hlHeight)
                    }
                }
            }
        }

        if (isSelected) {
            // Selection-Hintergrund zuerst zeichnen
            this.ctx.fillStyle = this._getHighlightBgColor(STYLES[Style.EDITOR_SELECTION])
            this.ctx.fillRect(x, hlY, hlW, hlHeight)
        }

        // Invertierte Vordergrundfarbe bei Selection ODER aktuellem Match im Nerd-Mode
        const needsInvert = isSelected || (isCurrentMatch && this.isNerdMode())
        this._renderCharWithoutBackground(x, y, { char: ch, style }, needsInvert)
    }

    /**
     * Ermittelt die Background-Farbe fuer einen Style basierend auf Display-Modus.
     * Konsolidiert die Farblogik fuer Selection und Search-Highlights.
     * @param {Object} style - Style-Objekt mit nerdBackground, darkBackground, lightBackground
     * @returns {string} CSS-Farbwert
     */
    _getHighlightBgColor(style) {
        if (this.isNerdMode()) {
            return style.nerdBackground
        } else if (this.colors.isColored) {
            return this.colors.isDarkMode ? style.darkBackground : style.lightBackground
        } else {
            return this.isLightBackground()
                ? style.lightBackground : style.darkBackground
        }
    }

    /**
     * Rendert ein Zeichen ohne Background zu zeichnen.
     * Wird verwendet wenn Selection-Background bereits gezeichnet wurde.
     *
     * @param {number} x - X-Position in Pixeln
     * @param {number} y - Y-Position in Pixeln
     * @param {Object} char - ScreenCharacter-Objekt
     * @param {boolean} isSelected - Für Nerd-Mode Farbumkehr
     */
    _renderCharWithoutBackground(x, y, char, isSelected = false) {
        const resolved = this._resolveStyle(char.style)
        let { color, isBold, isItalic, isStrikethrough, isUnderline, hasGlow } = resolved

        // Leerzeichen: Nur Dekorationen zeichnen, keinen Glyph
        if (char.char === ' ' || char.char === '') {
            if (isSelected && this.isNerdMode()) {
                color = STYLES[Style.EDITOR_SELECTION].nerdForeground || '#000000'
            }
            this._drawTextDecorations(x, y, this.charWidth, color, isStrikethrough, isUnderline)
            return
        }

        // Box-Drawing Characters speziell behandeln (kein Atlas)
        if (BOX_CHARS_SET.has(char.char)) {
            this._renderBoxChar(x, y, char)
            return
        }

        // Nerd-Mode: Bei Selection Vordergrundfarbe anpassen (schwarz auf grün)
        if (isSelected && this.isNerdMode()) {
            color = STYLES[Style.EDITOR_SELECTION].nerdForeground || '#000000'
        }

        // Kein Glow bei Selection
        const glowForAtlas = hasGlow && !isSelected

        // Glyph aus dem Atlas holen und per drawImage zeichnen
        // TopPad: Glyph-Canvas ist höher als charHeight — nach oben versetzt zeichnen
        const drawW = charDisplayWidth(char.char) === 2 ? 2 * this.charWidth : this.charWidth
        const glyph = this._getGlyph(char.char, color, isBold, isItalic, glowForAtlas)
        this.ctx.drawImage(glyph, x, y - this._glyphTopPad, drawW, this.charHeight + this._glyphTopPad)

        this._drawTextDecorations(x, y, drawW, color, isStrikethrough, isUnderline)
    }

    /**
     * Rendert nur den Overlay-Layer.
     * Malt für jede Zelle erst den Hintergrund, dann das Zeichen.
     * Öffentliche Methode für explizite Overlay-Rendering-Kontrolle.
     *
     * @param {Overlay} overlay
     */
    renderOverlay(overlay) {
        const frames = overlay.frames || []
        const stripes = overlay.stripes || []

        // 1. Frames: Background-Rect + Border-Path + Title-Strip
        //    (siehe docs/menu-inventory-and-concept.md §4 + Lesson 54).
        //    Nach Phase 5 hat jede Overlay-Session genau einen Frame —
        //    Cells und Spans liegen IMMER auf einem Frame-Background, kein
        //    pro-Cell-fillRect noetig (wuerde den Stripe ueberdecken).
        for (const frame of frames) {
            this._renderOverlayFrame(frame)
        }

        // 2. Selection-Stripes auf den Frame-Background legen, BEVOR Cells
        //    und Spans gerendert werden — sonst wuerde der Stripe den `[K]`-
        //    Bracket und das Label uebermalen.
        for (const stripe of stripes) {
            this._renderOverlayStripe(stripe)
        }

        // 3. Cells direkt zeichnen — Frame-Background ist schon da.
        for (const [key, char] of overlay.cells) {
            const commaIdx = key.indexOf(',')
            const x = parseInt(key.substring(0, commaIdx), 10)
            const y = parseInt(key.substring(commaIdx + 1), 10)
            const pixelX = x * this.charWidth
            const pixelY = y * this.charHeight

            if (char.char !== ' ' && char.char !== '') {
                this._renderChar(pixelX, pixelY, char)
            }
        }

        // 4. Text-Spans (proportional + mono Fallback). Spans, die explizit
        //    `clearBackground: true` setzen (Title-Spans auf Border-Linien),
        //    machen ihren eigenen Clear — sonst liegt der Span auf dem
        //    Frame-Background.
        for (const span of overlay.textSpans) {
            this._renderOverlayTextSpan(span)
        }

        // 5. Circular Control-Icons (Prop-Mode-Akzent fuer das Context-Menu).
        //    Nach Spans, weil Icons als visueller Akzent obenauf liegen sollen.
        const icons = overlay.icons || []
        for (const icon of icons) {
            this._renderOverlayControlIcon(icon)
        }

        // 6. Phosphor-Overlay (Gimmick fuer About): Mode-unabhaengig schwarzer
        //    BG + gruener Glow-Text. Liegt ueber allem.
        if (overlay.phosphorOverlay) {
            this._renderPhosphorOverlay(overlay.phosphorOverlay)
        }
    }

    /**
     * Rendert ein Context-Menu-Icon: Border-Kreis + Symbol darin, beide in
     * derselben Farbe. Deutlich kleiner als `_renderControlIcon` (das fuer
     * Pane-Border-Controls verwendet wird) — der Radius wird ueber einen
     * Verkleinerungs-Faktor reduziert, damit das Icon in einem 2-Cell-Gutter
     * Platz findet, ohne den Rest der Zeile zu dominieren.
     *
     * Eingabe: pixelgenauer Mittelpunkt (proportional praezise) + Grid-Zeile.
     *
     * @param {{pxCenterX: number, gridY: number, symbol: string, style: string}} icon
     */
    _renderOverlayControlIcon(icon) {
        const sizeFactor = 0.65  // Tuning in Schritt 7 (visuelle Feinjustierung)

        const cx = icon.pxCenterX
        const cy = icon.gridY * this.charHeight + this._verticalPad + this._gridFontSize / 2
        const r = Math.min(this.charWidth, this._gridFontSize / 2) * 0.95 * sizeFactor
        const scale = Math.max(1, this._scaleFactor)
        const lineWidth = 1 * scale

        const resolved = this._resolveStyle(icon.style)
        this.ctx.strokeStyle = resolved.color
        this.ctx.fillStyle = resolved.color
        this.ctx.lineWidth = lineWidth
        this.ctx.lineCap = 'round'

        if (resolved.hasGlow) {
            this.ctx.shadowColor = this.colors.glow.color
            this._setShadowBlur(this.colors.glow.blur)
        } else {
            this._setShadowBlur(0)
        }

        // Border-Kreis
        this.ctx.beginPath()
        this.ctx.arc(cx, cy, r, 0, 2 * Math.PI)
        this.ctx.stroke()

        // Symbol drinnen — derselbe Helper wie bei den Border-Controls
        this._drawControlSymbol(icon.symbol, cx, cy, r)

        this._setShadowBlur(0)
    }

    /**
     * Rendert eine Phosphor-Box: schwarzer Hintergrund, doppellinige gruene
     * Border, gruener Glow-Text. Mode-unabhaengig — bypassed das Style-System
     * und nutzt feste Phosphor-Farben aus DISPLAY_MODES[ULTRA_NERD].
     */
    _renderPhosphorOverlay(po) {
        const cw = this.charWidth
        const ch = this.charHeight
        const nerd = DISPLAY_MODES[DisplayMode.ULTRA_NERD]
        const PHOSPHOR_BG = nerd.background          // '#000000'
        const PHOSPHOR_FG = nerd.foreground[Intensity.HIGH]  // '#66ff66'
        const GLOW_COLOR = nerd.glow.color           // '#33ff33'
        const GLOW_BLUR = nerd.glow.blur             // 4

        const boxPxX = po.x * cw
        const boxPxY = po.y * ch
        const boxPxW = po.width * cw
        const boxPxH = po.height * ch
        const isProp = this.isProportional()

        // 1) Schwarzer BG-Rect, blickdicht ueber den Editor. Im Prop-Mode
        //    bis zur Aussenlinie der Doppelborder (cw/2 bzw. ch/2 nach innen
        //    geschoben) — sonst sieht der schwarze Rand ueber der Border-Linie
        //    hinaus aus wie eine "Schattenfuge", was hier unerwuenscht ist.
        //    Im Mono-Mode bleibt's das volle Grid-Rect, weil Mono-Boxen ohne
        //    Innenrand auskommen.
        this.ctx.fillStyle = PHOSPHOR_BG
        this._setShadowBlur(0)
        if (isProp) {
            const halfCw = cw / 2
            const halfCh = ch / 2
            this.ctx.fillRect(
                boxPxX + halfCw,
                boxPxY + halfCh,
                boxPxW - cw,
                boxPxH - ch
            )
        } else {
            this.ctx.fillRect(boxPxX, boxPxY, boxPxW, boxPxH)
        }

        // 2) Doppellinige Border: zwei konzentrische Rechtecke, 2 Pixel
        //    Abstand. Glow ueber shadowBlur.
        this.ctx.strokeStyle = PHOSPHOR_FG
        this.ctx.lineWidth = Math.max(1, this._scaleFactor)
        this.ctx.lineCap = 'square'
        this.ctx.shadowColor = GLOW_COLOR
        this.ctx.shadowBlur = GLOW_BLUR

        const inset = Math.floor(cw / 2)
        const insetY = Math.floor(ch / 2)
        const outerL = Math.floor(boxPxX + inset) + 0.5
        const outerR = Math.floor(boxPxX + boxPxW - inset) + 0.5
        const outerT = Math.floor(boxPxY + insetY) + 0.5
        const outerB = Math.floor(boxPxY + boxPxH - insetY) + 0.5
        const gap = 2

        this.ctx.beginPath()
        this.ctx.moveTo(outerL, outerT)
        this.ctx.lineTo(outerR, outerT)
        this.ctx.lineTo(outerR, outerB)
        this.ctx.lineTo(outerL, outerB)
        this.ctx.closePath()
        this.ctx.stroke()

        this.ctx.beginPath()
        this.ctx.moveTo(outerL + gap, outerT + gap)
        this.ctx.lineTo(outerR - gap, outerT + gap)
        this.ctx.lineTo(outerR - gap, outerB - gap)
        this.ctx.lineTo(outerL + gap, outerB - gap)
        this.ctx.closePath()
        this.ctx.stroke()

        // 3) Text-Lines, monospace, zentriert in der Box. Phosphor-Look
        //    erfordert Monospace-Font — Garamond/Noto-Sans waeren stilbruch.
        const fontSize = this._gridFontSize || this.fontSize
        this.ctx.font = `${fontSize}px 'DinaRemasterII', 'NotoSansMono', monospace`
        this._lastFont = null  // Font-Cache invalidieren, andere Render-Pfade setzen neu
        this.ctx.fillStyle = PHOSPHOR_FG
        this.ctx.textBaseline = 'top'
        this.ctx.textAlign = 'center'

        const centerX = boxPxX + boxPxW / 2
        po.lines.forEach((line, i) => {
            const lineY = (po.y + 1 + i) * ch + (this._verticalPad || 0)
            this.ctx.fillText(line, centerX, lineY)
        })

        // Cleanup: Shadow + textAlign zuruecksetzen, damit nachfolgende
        // Render-Pfade nicht beeinflusst werden.
        this.ctx.shadowBlur = 0
        this.ctx.textAlign = 'left'
        this.ctx.lineCap = 'butt'
    }

    /**
     * Loest den fillStyle fuer einen Overlay-Background-Style auf.
     * MENU_BACKGROUND und MENU_TITLE_BG werden Mode-bewusst auf den Editor-
     * Hintergrund abgebildet (blickdicht, ohne sichtbaren Akzent), damit
     * Editor-Text darunter zuverlaessig ueberdeckt ist. MENU_ITEM_SELECTED_BG
     * folgt dem Active-Row-Highlight des Editors — Konzept-Doc §5.
     */
    _resolveOverlayBgColor(styleName) {
        if (styleName === Style.MENU_ITEM_SELECTED_BG) {
            return this.colors.activeRowBackground || this.colors.background
        }
        // Scrollbar-Track/Thumb sind als Foreground-Styles definiert (echte
        // Color-Property im Style). Direkt deren color verwenden — sonst
        // fielen sie in den Frame-BG-Tint und waeren visuell unsichtbar.
        if (styleName === Style.SCROLLBAR_TRACK || styleName === Style.SCROLLBAR_THUMB) {
            return this._resolveStyle(styleName).color
        }
        // Default fuer alle anderen Background-Styles: leicht abgehobene
        // Variante des Editor-Hintergrunds (~5% Tint gegen Weiss bei dunklem
        // BG, gegen Schwarz bei hellem BG). Damit hebt sich die Menue-Flaeche
        // sichtbar vom Editor ab, ohne aufdringlich zu werden.
        //
        // Robust gegen Aufrufer, die versehentlich einen Foreground-Style
        // (z.B. MENU_BORDER) als bgStyle uebergeben — das blieb im alten
        // Cell-Layer folgenlos, weil bgStyle damals nicht in den fillStyle
        // floss. MENU_BACKGROUND und MENU_TITLE_BG landen ebenfalls hier.
        return this._overlayFrameBgColor()
    }

    /**
     * Berechnet die Menue-Frame-Hintergrundfarbe — 5% getintet gegen Weiss
     * (bei dunklem Editor-BG) oder Schwarz (bei hellem). Ermittelt die
     * Helligkeit aus dem Mode-Background, nicht aus `isLightBackground()`,
     * denn letzteres greift nur fuer monochrome Light-Modes.
     */
    _overlayFrameBgColor() {
        const bg = this.colors.background
        if (typeof bg !== 'string' || bg.length < 7) return bg
        const r = parseInt(bg.slice(1, 3), 16)
        const g = parseInt(bg.slice(3, 5), 16)
        const b = parseInt(bg.slice(5, 7), 16)
        const luminance = (r + g + b) / 3
        const tint = luminance > 127 ? '#000000' : '#ffffff'
        return mixHexColors(bg, tint, 0.04)
    }

    /**
     * Rendert einen Menue-Frame: Background-Rect + Border-Path +
     * Title-Strip. Geometrie unterscheidet sich Mono vs. Prop, damit das
     * BG-Rect im Proportional-Mode mit den variablen Editor-Glyphen
     * darunter „atmet" (siehe Konzept-Doc §4).
     */
    _renderOverlayFrame(frame) {
        const cw = this.charWidth
        const ch = this.charHeight
        const isProp = this.isProportional()

        const boxPxX = frame.x * cw
        const boxPxY = frame.y * ch
        const boxPxW = frame.width * cw
        const boxPxH = frame.height * ch

        // 1) Background-Rect — blickdichter Untergrund fuer den Frame.
        //    Im Prop-Mode ragt das BG-Rect 2 Pixel ueber die Border-Linie
        //    nach AUSSEN — die „Schattenfuge". Effekt: die Border-Linie
        //    sitzt auf einem 2px breiten Frame-BG-Streifen, abgesetzt vom
        //    Editor-BG dahinter. Die Border erscheint dadurch heraus-
        //    gehoben und der Frame-Rand wird klarer erkennbar.
        const bgColor = this._resolveOverlayBgColor(frame.bgStyle)
        this.ctx.fillStyle = bgColor
        if (isProp) {
            const halfCw = cw / 2
            const halfCh = ch / 2
            const fuge = 2
            this.ctx.fillRect(
                boxPxX + halfCw - fuge,
                boxPxY + halfCh - fuge,
                boxPxW - cw + 2 * fuge,
                boxPxH - ch + 2 * fuge
            )
        } else {
            this.ctx.fillRect(boxPxX, boxPxY, boxPxW, boxPxH)
        }

        // 2) Border-Path. Im Prop-Mode liegt die Linie auf der Mitte der
        //    Border-Cells (= Aussenkante des BG-Rects). Im Mono-Mode auf den
        //    Cell-Mitten, ebenfalls 1-Pixel-scharf via Half-Pixel-Snapping
        //    (Lesson 54). Wir setzen lineCap='square' fuer Mono, damit
        //    Ecken pixelgenau schliessen.
        const { color: borderColor } = this._resolveStyle(frame.borderStyle)
        const scale = Math.max(1, this._scaleFactor)
        this.ctx.strokeStyle = borderColor
        this.ctx.lineWidth = 1 * scale
        this.ctx.lineCap = isProp ? 'butt' : 'square'
        this._setShadowBlur(0)

        let leftX, rightX, topY, bottomY
        if (isProp) {
            leftX = Math.floor(boxPxX + cw / 2) + 0.5
            rightX = Math.floor(boxPxX + boxPxW - cw / 2) + 0.5
            topY = Math.floor(boxPxY + ch / 2) + 0.5
            bottomY = Math.floor(boxPxY + boxPxH - ch / 2) + 0.5
        } else {
            leftX = Math.floor(boxPxX + cw / 2) + 0.5
            rightX = Math.floor(boxPxX + boxPxW - cw / 2) + 0.5
            topY = Math.floor(boxPxY + this._verticalPad + this._gridFontSize / 2) + 0.5
            bottomY = Math.floor(boxPxY + boxPxH - ch + this._verticalPad + this._gridFontSize / 2) + 0.5
        }

        this.ctx.beginPath()
        this.ctx.moveTo(leftX, topY)
        this.ctx.lineTo(rightX, topY)
        this.ctx.lineTo(rightX, bottomY)
        this.ctx.lineTo(leftX, bottomY)
        this.ctx.lineTo(leftX, topY)
        this.ctx.stroke()
        this.ctx.lineCap = 'butt'

        // Title-Strip im Frame ist nicht noetig: drawMenuFrame emittiert den
        // Title als Text-Span mit `clearBackground: true`. Der Span malt
        // pixelgenau seinen eigenen blickdichten Hintergrund und uebermalt
        // dadurch die Border-Linie nur im tatsaechlich belegten Bereich.
        // Damit funktioniert sowohl Mono (Cell-genau) als auch Prop (Pixel-
        // genau, abhaengig von der Glyph-Breite).
    }

    /**
     * Rendert einen Selection-Stripe (vollflaechiges Hintergrund-Rect ueber
     * die Item-Breite). Liegt zwischen Frame und Cells/Spans.
     *
     * Standard: Pixel-Breite = stripe.width * charWidth (Mono-Geometrie). Im
     * Prop-Mode kann der Aufrufer optional `stripe.text` mitgeben — dann
     * wird die Pixel-Breite ueber `computeStringLayout` exakt der Text-Breite
     * angepasst (+2 Px Padding analog zum Span-Clear). Damit liegt der
     * Stripe von Toggle-Tabs etc. praezise unter dem proportional gerenderten
     * Text statt unter der ueberbreiten Mono-Char-Zahl.
     *
     * Wichtig: `stripe.textStyle` muss den Style des Text-Spans nennen,
     * damit `computeStringLayout` mit den korrekten Bold/Italic-Breiten
     * rechnet — sonst ist der Stripe bei fettem Text zu schmal.
     */
    _renderOverlayStripe(stripe) {
        const cw = this.charWidth
        const ch = this.charHeight
        const height = stripe.height || 1
        const width = stripe.width || 1

        // Sonderpfad: `monoChar` + Mono-Mode → render pro Cell mit Char-Glyph
        // (z.B. `░` fuer den Scrollbar-Track, optisch konsistent zum Editor-
        // und Console-Scrollbar). Im Prop-Mode faellt das durch zum fillRect.
        if (stripe.monoChar && !this.isProportional()) {
            const cellStyle = stripe.monoCharStyle || stripe.bgStyle
            for (let r = 0; r < height; r++) {
                for (let c = 0; c < width; c++) {
                    this._renderChar((stripe.x + c) * cw, (stripe.y + r) * ch,
                                     { char: stripe.monoChar, style: cellStyle })
                }
            }
            return
        }

        const pxY = stripe.y * ch
        // pixelX/pixelW erlauben sub-char-pixelgenaue Geometrie — vom
        // Dashboard genutzt, um Scrollbar-Track/Edges im Prop-Mode genau zu
        // positionieren (analog Editor-Minimap). Wenn nicht gesetzt, fallen
        // wir auf die Char-Grid-Berechnung zurueck.
        let pxX = stripe.pixelX !== undefined ? stripe.pixelX : stripe.x * cw
        let pxW = stripe.pixelW !== undefined ? stripe.pixelW : width * cw

        if (stripe.text && this.isProportional()) {
            const { cache, scaleFactor } = this._activeMetrics(4)
            if (cache) {
                const textStyle = stripe.textStyle || stripe.bgStyle
                const resolved = this._resolveStyle(textStyle)
                const codepoints = Array.from(stripe.text)
                const formatRuns = [{
                    start: 0, end: codepoints.length,
                    bold: resolved.isBold,
                    italic: resolved.isItalic,
                    underline: !!resolved.isUnderline,
                    strikethrough: !!resolved.isStrikethrough,
                    style: textStyle
                }]
                const layout = computeStringLayout(stripe.text, cache, cache.baseStep, formatRuns)
                pxW = Math.min(layout.totalWidth * scaleFactor + 2, pxW)
            }
        }

        const pxH = height * ch
        // `bgColor` (direkter Color-String) umgeht die bgStyle-Aufloesung —
        // wird vom Dashboard fuer Mid-Tone-Mischungen genutzt (Pseudo-Alpha
        // via opake RGB-Mischungen, kein globalAlpha).
        this.ctx.fillStyle = stripe.bgColor || this._resolveOverlayBgColor(stripe.bgStyle)
        this.ctx.fillRect(pxX, pxY, pxW, pxH)
    }

    /**
     * Rendert einen Text-Span im Overlay (proportional oder monospace Fallback).
     * Nach Phase 5: Frame liegt immer drunter, kein pro-Span-Background-Clear
     * mehr noetig — ausser bei `span.clearBackground === true` (Title-Spans
     * auf Border-Linien).
     * @param {Object} span - { x, y, text, style, maxWidth, cursorPos, cursorVisible, selectionStart, selectionEnd, align }
     */
    _renderOverlayTextSpan(span) {
        const pixelX = span.x * this.charWidth + (span.pixelOffsetX || 0)
        const pixelY = span.y * this.charHeight
        const maxPx = (span.maxWidth || 999) * this.charWidth - (span.pixelOffsetX || 0)
        const text = span.text || ''

        const { cache: overlayCache } = this._activeMetrics(4)
        if (this.isProportional() && overlayCache) {
            this._renderOverlayTextSpanProportional(span, text, pixelX, pixelY, maxPx)
        } else {
            this._renderOverlayTextSpanMono(span, text, pixelY)
        }
    }

    /**
     * Proportionales Rendering eines Overlay-Text-Spans.
     */
    _renderOverlayTextSpanProportional(span, text, pixelX, pixelY, maxPx) {
        const { cache: activeCache, scaleFactor: activeScale } = this._activeMetrics(4)
        const scale = activeScale
        const baseStep = activeCache.baseStep
        const resolved = this._resolveStyle(span.style)

        if (text.length > 0) {
            const codepoints = Array.from(text)
            // span.bold (true|false) ueberschreibt den Style-Default — wird
            // vom Dashboard genutzt, um Selection-Farbe einmal bold und einmal
            // nicht zu rendern (Name nur bold bei offenen Projekten).
            const boldEff = span.bold !== undefined ? span.bold : resolved.isBold
            const formatRuns = [{
                start: 0, end: codepoints.length,
                bold: boldEff, italic: resolved.isItalic,
                underline: !!resolved.isUnderline, strikethrough: !!resolved.isStrikethrough,
                style: span.style
            }]

            const layout = computeStringLayout(text, activeCache, baseStep, formatRuns)
            const totalWidth = layout.totalWidth * scale

            let startX = pixelX
            if (span.align === 'right') {
                startX = pixelX + maxPx - totalWidth
            } else if (span.align === 'center') {
                startX = pixelX + (maxPx - totalWidth) / 2
            }

            // Nach Phase 5: der Frame haelt den Hintergrund — kein
            // pro-Span-Clear in Editor-BG-Farbe mehr (wuerde den Selection-
            // Stripe ueberdecken). Spans, die auf einer Border-Linie sitzen
            // (Title-Spans bei SearchManager oder LinkPicker), setzen
            // `clearBackground: true`, damit der Border-Path unter ihnen
            // blickdicht ueberdeckt wird — obere Cell-Haelfte liegt im
            // Editor-BG, untere im Frame-BG (sonst entsteht ein sichtbarer
            // Streifen im Title-Bereich).
            if (span.clearBackground === true) {
                const clearW = Math.min(totalWidth + 2, maxPx)
                const topH = Math.floor(this.charHeight / 2)
                const botH = this.charHeight - topH
                this.ctx.fillStyle = this.colors.background
                this.ctx.fillRect(startX, pixelY, clearW, topH)
                this.ctx.fillStyle = this._overlayFrameBgColor()
                this.ctx.fillRect(startX, pixelY + topH, clearW, botH)
            }

            // Selection-Range vorbereiten
            const selRange = (span.selectionStart != null && span.selectionEnd != null
                && span.selectionStart !== span.selectionEnd)
                ? { start: Math.min(span.selectionStart, span.selectionEnd),
                    end: Math.max(span.selectionStart, span.selectionEnd) }
                : null

            this.renderProportionalLine(
                this.ctx, text, startX, pixelY, formatRuns, selRange, null, layout
            )

            // I-beam Cursor
            if (span.cursorPos >= 0 && span.cursorVisible !== false) {
                let cursorPx
                if (span.cursorPos < codepoints.length) {
                    cursorPx = startX + layout.positions[span.cursorPos] * scale
                } else {
                    cursorPx = startX + totalWidth
                }
                this._renderOverlayCursor(cursorPx, pixelY)
            }
        } else if (span.cursorPos >= 0 && span.cursorVisible !== false) {
            // Leerer Text, nur Cursor
            this._renderOverlayCursor(pixelX, pixelY)
        }
    }

    /**
     * Monospace-Fallback fuer Overlay-Text-Spans.
     */
    _renderOverlayTextSpanMono(span, text, pixelY) {
        if (text.length > 0) {
            const maxW = span.maxWidth || 999
            let baseX = span.x
            if (span.align === 'right') {
                baseX = span.x + (span.maxWidth || 0) - text.length
            } else if (span.align === 'center') {
                baseX = span.x + Math.floor(((span.maxWidth || 0) - text.length) / 2)
            }

            // Nach Phase 5: Frame haelt den Hintergrund — kein pro-Cell-Clear
            // mehr. Title-Spans auf Border-Linien setzen `clearBackground: true`
            // und brauchen Frame-BG-Farbe, weil im Mono-Mode die Border-Cell
            // komplett vom Frame-BG abgedeckt ist (kein Inset).
            const doClear = span.clearBackground === true
            for (let i = 0; i < text.length && i < maxW; i++) {
                const pxX = (baseX + i) * this.charWidth
                if (doClear) {
                    this.ctx.fillStyle = this._overlayFrameBgColor()
                    this.ctx.fillRect(pxX, pixelY, this.charWidth, this.charHeight)
                }
                this._renderChar(pxX, pixelY, { char: text[i], style: span.style, boldOverride: span.bold })
            }
        }

        // Block-Cursor
        if (span.cursorPos >= 0 && span.cursorVisible !== false) {
            const cX = (span.x + span.cursorPos) * this.charWidth
            this.ctx.fillStyle = this.colors.background
            this.ctx.fillRect(cX, pixelY, this.charWidth, this.charHeight)
            const cursorChar = span.cursorPos < text.length ? text[span.cursorPos] : '_'
            this._renderChar(cX, pixelY, { char: cursorChar, style: Style.EDITOR_CURSOR })
        }
    }

    /**
     * Rendert einen I-beam Cursor im Overlay (proportionaler Modus).
     */
    _renderOverlayCursor(pixelX, pixelY) {
        this.ctx.fillStyle = this.colors.cursor
        if (this.colors.glow.color) {
            this.ctx.shadowColor = this.colors.glow.color
            this.ctx.shadowBlur = this.colors.glow.blur
        }
        // Vertikal zentriert wie der Text
        pixelY += this._verticalPad
        const cursorH = this._textHeight || this.charHeight
        const ibeamW = this._getScaledStrokeWidth(0.10, 2)
        const halfW = Math.floor(ibeamW / 2)
        this.ctx.fillRect(pixelX - halfW, pixelY, ibeamW, cursorH)
        if (this.colors.glow.color) {
            this.ctx.shadowBlur = 0
        }
    }

    /**
     * Verschiebt den Canvas-Inhalt innerhalb eines Pane-Bereichs.
     * Wird für Scroll-Shift-Optimierung verwendet: statt alles neu zu zeichnen,
     * wird der bestehende Inhalt verschoben und nur die neuen Zeilen gerendert.
     *
     * @param {Object} innerBounds - Die innerBounds des Panes {x, y, width, height}
     * @param {number} deltaLines - Scroll-Delta in Zeilen (positiv = nach unten)
     */
    shiftPaneContent(innerBounds, shiftPixels) {
        const ib = innerBounds
        const dpr = window.devicePixelRatio || 1
        const pixelX = ib.x * this.charWidth
        const pixelY = ib.y * this.charHeight
        const pixelW = ib.width * this.charWidth
        const pixelH = ib.height * this.charHeight
        // Grid-Zentrierungs-Offset (device pixels). Dest-Koords werden durch den
        // Canvas-Transform automatisch versetzt; die Source-Koords (canvas-raw,
        // KEIN Transform) müssen manuell um den Offset verschoben werden, damit
        // drawImage(canvas, …) wirklich den Grid-Bereich liest und nicht den
        // Rand/Offset-Streifen.
        const offX = this._gridOffsetX
        const offY = this._gridOffsetY

        // Auf ganze Pixel runden — sub-pixel drawImage verursacht Interpolation/Unschaerfe
        shiftPixels = Math.round(shiftPixels)
        if (shiftPixels === 0) return

        if (shiftPixels > 0) {
            // Nach unten gescrollt: Inhalt nach oben verschieben
            const srcY = pixelY + shiftPixels
            const srcH = pixelH - shiftPixels
            if (srcH > 0) {
                this.ctx.drawImage(this.canvas,
                    pixelX * dpr + offX, srcY * dpr + offY,
                    pixelW * dpr, srcH * dpr,
                    pixelX, pixelY, pixelW, srcH
                )
            }
            // Untere Zeilen mit Hintergrundfarbe löschen
            this.ctx.fillStyle = this.colors.background
            this.ctx.fillRect(pixelX, pixelY + srcH, pixelW, shiftPixels)
        } else {
            // Nach oben gescrollt: Inhalt nach unten verschieben
            const absShift = -shiftPixels
            const srcH = pixelH - absShift
            if (srcH > 0) {
                this.ctx.drawImage(this.canvas,
                    pixelX * dpr + offX, pixelY * dpr + offY,
                    pixelW * dpr, srcH * dpr,
                    pixelX, pixelY + absShift, pixelW, srcH
                )
            }
            // Obere Zeilen mit Hintergrundfarbe löschen
            this.ctx.fillStyle = this.colors.background
            this.ctx.fillRect(pixelX, pixelY, pixelW, absShift)
        }
    }

    /**
     * Berechnet die Character-Position aus Pixel-Koordinaten.
     *
     * @param {MouseEvent} event
     * @returns {{charX: number, charY: number}}
     */
    getCharPosition(event) {
        const rect = this.canvas.getBoundingClientRect()
        const dpr = window.devicePixelRatio || 1
        // Grid-Offset ist in device pixels gespeichert, hier zurück auf CSS-Pixel.
        // Klicks im Zentrier-Rand ergeben negative pixelX/Y und charX/Y = -1 —
        // Aufrufer behandeln das bereits als "außerhalb".
        const pixelX = event.clientX - rect.left - this._gridOffsetX / dpr
        const pixelY = event.clientY - rect.top - this._gridOffsetY / dpr

        return {
            charX: Math.floor(pixelX / this.charWidth),
            charY: Math.floor(pixelY / this.charHeight),
            pixelX,                                        // Rohe Pixel-Position für proportionalen Text
            pixelY,                                        // Rohe Pixel-Position
            fractionalCharY: pixelY / this.charHeight
        }
    }

    /**
     * Setzt eine Clip-Region für einen Pane-Bereich.
     * Verhindert Rendering außerhalb der Pane-Grenzen (für teilweise sichtbare Zeilen).
     * @param {Object} innerBounds - {x, y, width, height} in Character-Koordinaten
     */
    setClipRect(innerBounds) {
        const ib = innerBounds
        this.ctx.save()
        this.ctx.beginPath()
        this.ctx.rect(
            ib.x * this.charWidth,
            ib.y * this.charHeight,
            ib.width * this.charWidth,
            ib.height * this.charHeight
        )
        this.ctx.clip()
    }

    /**
     * Entfernt die aktive Clip-Region.
     */
    clearClipRect() {
        this.ctx.restore()
    }

    /**
     * Leert den Canvas mit der Hintergrundfarbe.
     */
    _clear() {
        // Identity-Transform für den Full-Clear: deckt das gesamte Canvas-Backing
        // inklusive der Grid-Offset-Streifen rund ums Grid ab. Mit dem normalen
        // Grid-Transform würde fillRect(0,0,innerW,innerH) oben-links einen Streifen
        // von (offsetX × offsetY) überspringen und unten-rechts rausragen.
        this.ctx.save()
        this.ctx.setTransform(1, 0, 0, 1, 0, 0)
        this.ctx.fillStyle = this.colors.background
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
        this.ctx.restore()
    }

    /**
     * Rendert das Character-Grid.
     * Overlay-Zeichen überschreiben Screen-Zeichen an der gleichen Position.
     *
     * Legacy-Pfad: Nur ohne Layout-Manager erreichbar (wird produktiv nicht aufgerufen).
     * Behalten als Fallback für Testing und Grundgerüst-Verifikation.
     *
     * @param {Screen} screen - Das Screen-Objekt
     * @param {Cursor} cursor - Der Cursor
     * @param {Overlay} overlay - Optionaler Overlay-Layer
     */
    _renderGrid(screen, cursor, overlay) {
        for (let y = 0; y < screen.rows; y++) {
            for (let x = 0; x < screen.cols; x++) {
                // Overlay hat Priorität über Screen
                let char = null
                let isOverlay = false

                if (overlay && overlay.hasCharAt(x, y)) {
                    char = overlay.getChar(x, y)
                    isOverlay = true
                } else {
                    char = screen.getChar(x, y)
                }

                if (!char) continue

                // Position in Pixeln
                const pixelX = x * this.charWidth
                const pixelY = y * this.charHeight

                // Cursor nur auf Screen anzeigen, nicht auf Overlay
                const isCursorHere = !isOverlay && cursor.isAt(x, y) && cursor.visible

                if (isCursorHere) {
                    this._renderCursorCell(pixelX, pixelY, char)
                } else {
                    this._renderChar(pixelX, pixelY, char)
                }
            }
        }
    }

    /**
     * Zeichnet Strikethrough- und Underline-Dekorationen für ein Zeichen.
     * Zentralisiert das duplizierte Dekorations-Muster aus _renderChar
     * und _renderCharWithoutBackground (je Space- und Glyph-Zweig).
     *
     * @param {number} x - X-Position in Pixeln
     * @param {number} y - Y-Position in Pixeln
     * @param {number} width - Breite der Zelle in Pixeln (charWidth oder proportionale Breite)
     * @param {string} color - Strichfarbe
     * @param {boolean} isStrikethrough - Durchgestrichener Text
     * @param {boolean} isUnderline - Unterstrichener Text
     */
    _drawTextDecorations(x, y, width, color, isStrikethrough, isUnderline) {
        if (isStrikethrough) {
            this.ctx.strokeStyle = color
            this.ctx.lineWidth = 2
            const middleY = y + this._strikethroughY
            this.ctx.beginPath()
            this.ctx.moveTo(x, middleY)
            this.ctx.lineTo(x + width, middleY)
            this.ctx.stroke()
        }
        if (isUnderline) {
            this.ctx.strokeStyle = color
            this.ctx.lineWidth = 1
            const underlineY = y + this._underlineY
            this.ctx.beginPath()
            this.ctx.moveTo(x, underlineY)
            this.ctx.lineTo(x + width, underlineY)
            this.ctx.stroke()
        }
    }

    /**
     * Rendert ein einzelnes Zeichen.
     * Erkennt Box-Drawing Characters und zeichnet sie als Linien.
     * Unterstützt Italic (Transformation), Strikethrough und Underline.
     *
     * @param {number} x - X-Position in Pixeln
     * @param {number} y - Y-Position in Pixeln
     * @param {Object} char - ScreenCharacter-Objekt
     */
    _renderChar(x, y, char) {
        const resolved = this._resolveStyle(char.style)

        // Hintergrund zeichnen wenn vorhanden
        if (resolved.backgroundColor) {
            this.ctx.fillStyle = resolved.backgroundColor
            this.ctx.fillRect(x, y, this.charWidth, this.charHeight)
        }

        const { color, isItalic, isStrikethrough, isUnderline, hasGlow } = resolved
        // Bold-Override: char.boldOverride === true|false ueberschreibt
        // den Style-Default. Wird vom Overlay-Text-Span-Pfad genutzt, um
        // z.B. dieselbe Selection-Farbe einmal bold und einmal nicht zu
        // rendern (Dashboard: Selektion zeigt Bold-Namen nur fuer offene
        // Projekte).
        const isBold = char.boldOverride !== undefined ? char.boldOverride : resolved.isBold

        // Leerzeichen: Nur Dekorationen zeichnen, keinen Glyph
        if (char.char === ' ' || char.char === '') {
            this._drawTextDecorations(x, y, this.charWidth, color, isStrikethrough, isUnderline)
            return
        }

        // Box-Drawing Characters speziell behandeln (kein Atlas)
        if (BOX_CHARS_SET.has(char.char)) {
            this._renderBoxChar(x, y, char)
            return
        }

        // Glyph aus dem Atlas holen und per drawImage zeichnen
        const glyph = this._getGlyph(char.char, color, isBold, isItalic, hasGlow)
        this.ctx.drawImage(glyph, x, y - this._glyphTopPad, this.charWidth, this.charHeight + this._glyphTopPad)

        this._drawTextDecorations(x, y, this.charWidth, color, isStrikethrough, isUnderline)
    }

    /**
     * Rendert ein Box-Drawing Character als Linien.
     * Wird pixel-genau in die Zelle gezeichnet.
     *
     * @param {number} x - X-Position in Pixeln
     * @param {number} y - Y-Position in Pixeln
     * @param {Object} char - ScreenCharacter-Objekt
     */
    _renderBoxChar(x, y, char) {
        const { color } = this._resolveStyle(char.style)

        this.ctx.strokeStyle = color
        this.ctx.fillStyle = color

        // Box-Drawing-Glyphen ohne Glow: Live-Shadow-Filter pro Stroke-Operation
        // triggert auf Retina-4K einen GPU/Compositor-Stall. Pane-Borders und
        // Outline-Tree-Linien laufen deshalb über Path-Batches (Lesson 54),
        // _renderBoxChar bleibt nur noch für Content-Box-Chars wie ═══ im
        // Background-Separator — dort sind es wenige Chars pro Frame, kein Stall.
        this._setShadowBlur(0)

        // Zellenmaße
        const w = this.charWidth
        const h = this.charHeight
        const cx = x + w / 2  // Zellenmitte X
        // Vertikale Mitte: Font-Block zentriert in der Zelle,
        // Querlinien auf Höhe der Textmitte
        const cy = y + this._verticalPad + this._gridFontSize / 2

        // Liniendicke: skaliert mit Zoom-Faktor (dünn für SINGLE, dicker für DOUBLE)
        const isDouble = DOUBLE_BOX_CHARS_SET.has(char.char)
        const scale = Math.max(1, this._scaleFactor)
        const lineWidth = isDouble ? 2 * scale : 1 * scale
        const gap = isDouble ? 3 * scale : 0

        this.ctx.lineWidth = lineWidth
        this.ctx.lineCap = 'square'

        // Zeichne basierend auf Character-Typ
        this._drawBoxLines(char.char, x, y, w, h, cx, cy, gap)

        this._setShadowBlur(0)
    }

    /**
     * Rendert ein Control-Icon als Canvas-gezeichneten Kreis mit Symbol.
     * Nur im Proportional Mode — ersetzt die Text-Controls "(+)", "(-)" etc.
     * Analog zu _renderBoxChar(): Canvas-Primitive statt Font-Glyphen.
     *
     * Das Icon belegt 3 Grid-Positionen, ist visuell 2 Zellen breit,
     * zentriert auf die mittlere der 3 Zellen.
     *
     * @param {number} gridX - Grid-Spalte der MITTLEREN Zelle (= control.x)
     * @param {number} gridY - Grid-Zeile
     * @param {string} symbolChar - Symbol im Kreis: +, -, =, o, ●, x, ' ', v, <, >
     * @param {string} symbolStyle - Style für das Symbol (z.B. CONTROL_NORMAL)
     * @param {string} borderStyle - Style für den Kreisrand (z.B. PANE_BORDER_ACTIVE)
     * @param {number} sizeScale - Größenfaktor des Icons (1 = Standard; <1 für die
     *                             kleineren Outline-Note-Icons). Skaliert Radius +
     *                             Symbol um die unveränderte Kreismitte.
     */
    _renderControlIcon(gridX, gridY, symbolChar, symbolStyle, borderStyle, sizeScale = 1) {
        const w = this.charWidth
        const cx = gridX * w + w / 2
        const cy = gridY * this.charHeight + this._verticalPad + this._gridFontSize / 2
        const r = Math.min(w, this._gridFontSize / 2) * 0.95 * sizeScale
        const scale = Math.max(1, this._scaleFactor)
        const lineWidth = 1 * scale

        // Kreisrand mit Border-Farbe
        const borderResolved = this._resolveStyle(borderStyle)
        this.ctx.strokeStyle = borderResolved.color
        this.ctx.lineWidth = lineWidth
        this.ctx.lineCap = 'round'

        if (borderResolved.hasGlow) {
            this.ctx.shadowColor = this.colors.glow.color
            this._setShadowBlur(this.colors.glow.blur)
        } else {
            this._setShadowBlur(0)
        }

        this.ctx.beginPath()
        this.ctx.arc(cx, cy, r, 0, 2 * Math.PI)
        this.ctx.stroke()

        // Symbol mit eigener Farbe (Bold ignorieren — Icons nie bold)
        const symbolResolved = this._resolveStyle(symbolStyle)
        this.ctx.strokeStyle = symbolResolved.color
        this.ctx.fillStyle = symbolResolved.color
        this.ctx.lineWidth = lineWidth

        this._drawControlSymbol(symbolChar, cx, cy, r)

        this._setShadowBlur(0)
    }

    /**
     * Zeichnet das Symbol innerhalb eines Control-Icon-Kreises.
     *
     * @param {string} ch - Symbol-Zeichen
     * @param {number} cx - Kreismitte X (Pixel)
     * @param {number} cy - Kreismitte Y (Pixel)
     * @param {number} r - Kreisradius
     * @param {number} scale - Zoom-Skalierungsfaktor
     */
    _drawControlSymbol(ch, cx, cy, r) {
        const s = r * 0.45

        const line = (x1, y1, x2, y2) => {
            this.ctx.beginPath()
            this.ctx.moveTo(x1, y1)
            this.ctx.lineTo(x2, y2)
            this.ctx.stroke()
        }

        switch (ch) {
            case '+':
                line(cx - s, cy, cx + s, cy)
                line(cx, cy - s, cx, cy + s)
                break
            case '-':
                line(cx - s, cy, cx + s, cy)
                break
            case '=': {
                const gap = r * 0.15
                line(cx - s, cy - gap, cx + s, cy - gap)
                line(cx - s, cy + gap, cx + s, cy + gap)
                break
            }
            case 'o':
                this.ctx.beginPath()
                this.ctx.arc(cx, cy, r * 0.25, 0, 2 * Math.PI)
                this.ctx.stroke()
                break
            case '●':
                this.ctx.beginPath()
                this.ctx.arc(cx, cy, r * 0.35, 0, 2 * Math.PI)
                this.ctx.fill()
                break
            case 'x':
                line(cx - s, cy - s, cx + s, cy + s)
                line(cx + s, cy - s, cx - s, cy + s)
                break
            case '!': {
                // Senkrechter Strich + Punkt darunter
                line(cx, cy - s, cx, cy + s * 0.25)
                const dotR = Math.max(1, s * 0.18)
                this.ctx.beginPath()
                this.ctx.arc(cx, cy + s * 0.7, dotR, 0, 2 * Math.PI)
                this.ctx.fill()
                break
            }
            case ' ':
                // Leerer Kreis — nichts zeichnen
                break
            case 'v':
                this.ctx.beginPath()
                this.ctx.moveTo(cx - s, cy - s * 0.3)
                this.ctx.lineTo(cx - s * 0.2, cy + s * 0.6)
                this.ctx.lineTo(cx + s, cy - s * 0.7)
                this.ctx.stroke()
                break
            case '<':
                this.ctx.beginPath()
                this.ctx.moveTo(cx + s * 0.35, cy - s)
                this.ctx.lineTo(cx - s * 0.35, cy)
                this.ctx.lineTo(cx + s * 0.35, cy + s)
                this.ctx.stroke()
                break
            case '>':
                this.ctx.beginPath()
                this.ctx.moveTo(cx - s * 0.35, cy - s)
                this.ctx.lineTo(cx + s * 0.35, cy)
                this.ctx.lineTo(cx - s * 0.35, cy + s)
                this.ctx.stroke()
                break
        }
    }

    /**
     * Strokt eine Batch von Outline-Baumlinien — vertikale UND horizontale
     * Segmente, gruppiert nach Style als EIN Path pro Style. Ersetzt die
     * frühere Per-Call-API `drawVerticalConnector` und zieht die Tree-Box-Chars
     * (│├┤└┘) komplett in den Path-Pfad, damit highlighted Linien keine
     * Knubbel mehr an Box-Char-Gelenken zeigen (Glow-Unterschied zu live
     * gezeichneten Atlas-losen Box-Chars).
     *
     * @param {Array<{col:number, fromPx:number, toPx:number, styleName:string}>} verticals
     * @param {Array<{x0:number, x1:number, y:number, styleName:string}>} [horizontals]
     */
    drawBatchedConnectors(verticals, horizontals) {
        const hasV = verticals && verticals.length > 0
        const hasH = horizontals && horizontals.length > 0
        if (!hasV && !hasH) return

        const grouped = new Map()
        const bucket = (styleName) => {
            let b = grouped.get(styleName)
            if (!b) {
                b = { v: [], h: [] }
                grouped.set(styleName, b)
            }
            return b
        }
        if (hasV) {
            for (const s of verticals) {
                if (s.fromPx >= s.toPx) continue
                bucket(s.styleName).v.push(s)
            }
        }
        if (hasH) {
            for (const s of horizontals) {
                if (s.x0 >= s.x1) continue
                bucket(s.styleName).h.push(s)
            }
        }
        if (grouped.size === 0) return

        const cw = this.charWidth
        const scale = Math.max(1, this._scaleFactor)
        // lineWidth auf ganze Device-Pixel snappen, Center-Koordinate je nach
        // Parität aligned: gerade Device-lineWidth → Device-Pixel-Grenze
        // (CSS = integer Device / dpr), ungerade → Device-Pixel-Center (CSS =
        // (integer Device + 0.5) / dpr). Sonst verteilt Canvas die Linie per
        // Anti-Aliasing über mehrere Device-Pixel, und Safari wählt die
        // Intensitätsverteilung je nach Canvas-State (nach `drawImage(canvas,…)`
        // im Shift-Pfad anders als beim Full-Render) → sichtbarer Dicken-Jitter
        // beim Scrollen in Safari. Chrome/Firefox rastern konsistent, Safari nicht.
        const dpr = window.devicePixelRatio || 1
        const lineWidthDevice = Math.max(1, Math.round(scale * dpr))
        const lineWidth = lineWidthDevice / dpr
        const edgeOffsetCSS = (lineWidthDevice % 2 === 0) ? 0 : 0.5 / dpr

        for (const [styleName, segs] of grouped) {
            const { color, hasGlow } = this._resolveStyle(styleName)
            this.ctx.strokeStyle = color
            this.ctx.lineWidth = lineWidth
            // butt statt square: bei Zeilensegmenten, die aneinander stoßen,
            // erzeugt square eine Überlappung von lineWidth/2 an jedem Ende —
            // Safari verstärkt das mit Shadow-Blur zu sichtbaren Knubbeln.
            // Endpunkte sind per Math.round() auf Pixel-Grid gesnapped, also
            // schließen butt-Segmente nahtlos an.
            this.ctx.lineCap = 'butt'
            if (hasGlow) {
                this.ctx.shadowColor = this.colors.glow.color
                this._setShadowBlur(this.colors.glow.blur)
            } else {
                this._setShadowBlur(0)
            }

            this.ctx.beginPath()
            // Vertikale: cx snap auf Device-Pixel-Grenze bzw. -Center je nach
            // lineWidth-Parität. Endpunkte auf integer CSS (=gerade Device).
            for (const seg of segs.v) {
                const centerCSS = seg.col * cw + cw / 2
                const cx = Math.round(centerCSS * dpr) / dpr + edgeOffsetCSS
                this.ctx.moveTo(cx, Math.round(seg.fromPx))
                this.ctx.lineTo(cx, Math.round(seg.toPx))
            }
            // Horizontale: y analog zu cx — Parity-aware Snap. x-Endpunkte
            // auf integer CSS.
            for (const seg of segs.h) {
                const y = Math.round(seg.y * dpr) / dpr + edgeOffsetCSS
                this.ctx.moveTo(Math.round(seg.x0), y)
                this.ctx.lineTo(Math.round(seg.x1), y)
            }
            this.ctx.stroke()
        }

        this._setShadowBlur(0)
    }

    /**
     * Y-Pixel der horizontalen Mittellinie eines Box-Chars in einer gegebenen
     * Zelle (entspricht `cy` in _drawBoxLines). Wird von OutlineView für die
     * Positionierung horizontaler Tree-Linien-Stummel (├┤└┘) im Batch-Pfad
     * gebraucht.
     */
    boxCharCenterY(cellTopPx) {
        return cellTopPx + this._verticalPad + this._gridFontSize / 2
    }

    /**
     * Y-Pixel der Mitte bzw. Unterkante eines Control-Icons (Kreis) in einer
     * Grid-Zeile — single source of truth mit `_renderControlIcon`. Wird von
     * OutlineView für die Connector-Brücke unter dem (-)/(+)-Note-Icon gebraucht,
     * damit die Linie exakt am Kreisrand ansetzt (nicht an der Zeilen-Mitte).
     * @param {number} gridY - Grid-Zeile (fraktional erlaubt)
     * @param {number} sizeScale - Größenfaktor wie an _renderControlIcon
     * @returns {{centerY:number, radius:number, bottomY:number}}
     */
    controlIconGeometry(gridY, sizeScale = 1) {
        const centerY = gridY * this.charHeight + this._verticalPad + this._gridFontSize / 2
        const radius = Math.min(this.charWidth, this._gridFontSize / 2) * 0.95 * sizeScale
        return { centerY, radius, bottomY: centerY + radius }
    }

    /**
     * Zeichnet die Linien für einen Box-Drawing Character.
     *
     * @param {string} ch - Das Box-Character
     * @param {number} x - X-Position
     * @param {number} y - Y-Position
     * @param {number} w - Zellenbreite
     * @param {number} h - Zellenhöhe
     * @param {number} cx - Zellenmitte X
     * @param {number} cy - Zellenmitte Y
     * @param {number} gap - Abstand für Doppellinien
     */
    _drawBoxLines(ch, x, y, w, h, cx, cy, gap) {
        // Hilfsfunktion für Linien
        const line = (x1, y1, x2, y2) => {
            this.ctx.beginPath()
            this.ctx.moveTo(x1, y1)
            this.ctx.lineTo(x2, y2)
            this.ctx.stroke()
        }

        // Für Doppellinien: zwei parallele Linien zeichnen
        const doubleLine = (x1, y1, x2, y2, horizontal) => {
            const offset = gap / 2
            if (horizontal) {
                line(x1, y1 - offset, x2, y2 - offset)
                line(x1, y1 + offset, x2, y2 + offset)
            } else {
                line(x1 - offset, y1, x2 - offset, y2)
                line(x1 + offset, y1, x2 + offset, y2)
            }
        }

        // Single line characters
        const S = BoxChars.SINGLE
        const D = BoxChars.DOUBLE

        // Mapping: welche Linienrichtungen fuer jeden Character
        // Richtungen: top, right, bottom, left (von Zellenmitte aus)
        switch (ch) {
            // === SINGLE LINES ===
            case S.HORIZONTAL:
                line(x, cy, x + w, cy)
                break
            case S.VERTICAL:
                line(cx, y, cx, y + h)
                break
            case S.TOP_LEFT:
                line(cx, cy, x + w, cy)  // nach rechts
                line(cx, cy, cx, y + h)  // nach unten
                break
            case S.TOP_RIGHT:
                line(x, cy, cx, cy)      // nach links
                line(cx, cy, cx, y + h)  // nach unten
                break
            case S.BOTTOM_LEFT:
                line(cx, cy, x + w, cy)  // nach rechts
                line(cx, y, cx, cy)      // nach oben
                break
            case S.BOTTOM_RIGHT:
                line(x, cy, cx, cy)      // nach links
                line(cx, y, cx, cy)      // nach oben
                break
            case S.T_DOWN:
                line(x, cy, x + w, cy)   // horizontal durch
                line(cx, cy, cx, y + h)  // nach unten
                break
            case S.T_UP:
                line(x, cy, x + w, cy)   // horizontal durch
                line(cx, y, cx, cy)      // nach oben
                break
            case S.T_RIGHT:
                line(cx, y, cx, y + h)   // vertikal durch
                line(cx, cy, x + w, cy)  // nach rechts
                break
            case S.T_LEFT:
                line(cx, y, cx, y + h)   // vertikal durch
                line(x, cy, cx, cy)      // nach links
                break
            case S.CROSS:
                line(x, cy, x + w, cy)   // horizontal durch
                line(cx, y, cx, y + h)   // vertikal durch
                break

            // === DOUBLE LINES ===
            case D.HORIZONTAL:
                doubleLine(x, cy, x + w, cy, true)
                break
            case D.VERTICAL:
                doubleLine(cx, y, cx, y + h, false)
                break
            case D.TOP_LEFT:
                doubleLine(cx, cy, x + w, cy, true)
                doubleLine(cx, cy, cx, y + h, false)
                break
            case D.TOP_RIGHT:
                doubleLine(x, cy, cx, cy, true)
                doubleLine(cx, cy, cx, y + h, false)
                break
            case D.BOTTOM_LEFT:
                doubleLine(cx, cy, x + w, cy, true)
                doubleLine(cx, y, cx, cy, false)
                break
            case D.BOTTOM_RIGHT:
                doubleLine(x, cy, cx, cy, true)
                doubleLine(cx, y, cx, cy, false)
                break
            case D.T_DOWN:
                doubleLine(x, cy, x + w, cy, true)
                doubleLine(cx, cy, cx, y + h, false)
                break
            case D.T_UP:
                doubleLine(x, cy, x + w, cy, true)
                doubleLine(cx, y, cx, cy, false)
                break
            case D.T_RIGHT:
                doubleLine(cx, y, cx, y + h, false)
                doubleLine(cx, cy, x + w, cy, true)
                break
            case D.T_LEFT:
                doubleLine(cx, y, cx, y + h, false)
                doubleLine(x, cy, cx, cy, true)
                break
            case D.CROSS:
                doubleLine(x, cy, x + w, cy, true)
                doubleLine(cx, y, cx, y + h, false)
                break
        }
    }

    /**
     * Rendert eine Zelle mit Cursor (invertiert).
     *
     * @param {number} x - X-Position in Pixeln
     * @param {number} y - Y-Position in Pixeln
     * @param {Object} char - ScreenCharacter-Objekt
     */
    _renderCursorCell(x, y, char) {
        this.ctx.fillStyle = this.colors.cursor
        if (this.colors.glow.color) {
            this.ctx.shadowColor = this.colors.glow.color
            this._setShadowBlur(this.colors.glow.blur)
        }
        this.ctx.fillRect(x, y, this.charWidth, this.charHeight)
        this._setShadowBlur(0)

        if (char.char !== ' ' && char.char !== '') {
            const { isBold } = this._resolveStyle(char.style)
            this._setFont(isBold)
            this.ctx.fillStyle = this.colors.background
            const baselineY = y + this._verticalPad + this._gridFontSize * 0.85
            this.ctx.fillText(char.char, x, baselineY)
        }
    }

    /**
     * Wechselt den Display Mode.
     *
     * @param {string} mode - Der neue Display Mode (aus DisplayMode enum)
     */
    setDisplayMode(mode) {
        if (DISPLAY_MODES[mode]) {
            this.currentDisplayMode = mode
            this._updateColors()
            this._styleCache.clear()
            this._borderDirty = true
            this._invalidateGlyphAtlas()
        }
    }

    /**
     * Gibt den aktuellen Display Mode zurück.
     *
     * @returns {string}
     */
    getDisplayMode() {
        return this.currentDisplayMode
    }

    /**
     * Baut den Metriken-Cache für proportionale Fonts.
     * Wird nur aus setFont() aufgerufen, nicht bei Resize/Zoom.
     */
    _buildMetricsCache() {
        const fontConfig = FONTS[this.currentFont]
        this._metricsCache = buildMetricsCache(fontConfig, REFERENCE_SIZE)
        // scaleFactor basiert auf _gridFontSize (4-QL-Basis)
        this._scaleFactor = this._gridFontSize / REFERENCE_SIZE

        // System-Font MetricsCache (z.B. NotoSans fuer Garamond-Labels bei 4QL)
        if (fontConfig.systemFont && FONTS[fontConfig.systemFont]) {
            const systemConfig = FONTS[fontConfig.systemFont]
            this._systemMetricsCache = buildMetricsCache(systemConfig, REFERENCE_SIZE)
            this._systemScaleFactor = this._gridFontSize / REFERENCE_SIZE
        } else {
            this._systemMetricsCache = null
        }
    }

    /**
     * Wechselt den Font.
     *
     * @param {string} font - Der neue Font (aus FontFamily enum)
     * @returns {{cols: number, rows: number}} Neue Grid-Dimensionen
     */
    async setFont(font) {
        if (FONTS[font]) {
            // Fonts laden BEVOR currentFont gesetzt wird —
            // so sieht der Render-Loop während des await den alten Font
            await this._waitForFonts(font)

            // Jetzt atomar umschalten: currentFont + Messung + Cache in einem Zug
            // _measureCharacter() ZUERST: setzt _gridFontSize (Prop: 2/3 von fontSize)
            // _buildMetricsCache() DANACH: braucht _gridFontSize für _scaleFactor
            this.currentFont = font
            this._measureCharacter()
            if (this.isProportional()) {
                this._buildMetricsCache()
            } else {
                this._metricsCache = null
                this._systemMetricsCache = null
            }
            this._borderDirty = true
            this._invalidateGlyphAtlas()

            return this.calculateGridDimensions()
        }
        return this.calculateGridDimensions()
    }

    /**
     * Gibt den aktuellen Font zurück.
     *
     * @returns {string}
     */
    getFont() {
        return this.currentFont
    }

    /**
     * Ändert die Font-Größe und berechnet Character-Dimensionen neu.
     *
     * @param {number} newSize - Neue Font-Größe in px
     * @returns {{cols: number, rows: number}} Neue Grid-Dimensionen
     */
    setFontSize(newSize) {
        this.fontSize = Math.max(FONT_SIZE.min, Math.min(FONT_SIZE.max, newSize))
        this._measureCharacter()
        this._borderDirty = true
        this._invalidateGlyphAtlas()

        // Proportional: Skalierungsfaktoren aktualisieren (kein Cache-Rebuild)
        if (this.isProportional()) {
            this._scaleFactor = this._gridFontSize / REFERENCE_SIZE
            if (this._systemMetricsCache) {
                this._systemScaleFactor = this._gridFontSize / REFERENCE_SIZE
            }
        }

        // Canvas-Pixel vom vorherigen Zoom-Stand loswerden: canvas.width=… setzt
        // den Inhalt zurück (transparent). Außerdem wird der Grid-Offset neu mit
        // der frisch gemessenen charWidth/Height berechnet → Grid bleibt mittig.
        this._updateCanvasSize()

        return this.calculateGridDimensions()
    }

    /**
     * Behandelt Window-Resize.
     *
     * @returns {{cols: number, rows: number}} Neue Grid-Dimensionen
     */
    handleResize() {
        this._updateCanvasSize()
        this._measureCharacter()
        this._invalidateGlyphAtlas()
        return this.calculateGridDimensions()
    }

    /**
     * Zoom in (Font größer).
     *
     * @returns {{cols: number, rows: number}} Neue Grid-Dimensionen
     */
    zoomIn() {
        return this.setFontSize(this.fontSize + FONT_SIZE.step)
    }

    /**
     * Zoom out (Font kleiner).
     *
     * @returns {{cols: number, rows: number}} Neue Grid-Dimensionen
     */
    zoomOut() {
        return this.setFontSize(this.fontSize - FONT_SIZE.step)
    }

    /**
     * Zoom zurücksetzen auf Standard.
     *
     * @returns {{cols: number, rows: number}} Neue Grid-Dimensionen
     */
    zoomReset() {
        return this.setFontSize(FONT_SIZE.default)
    }

    /**
     * Gibt die aktuelle Font-Größe zurück.
     *
     * @returns {number}
     */
    getFontSize() {
        return this.fontSize
    }

    /**
     * Gibt die Character-Dimensionen zurück.
     *
     * @returns {{width: number, height: number}}
     */
    getCharDimensions() {
        return {
            width: this.charWidth,
            height: this.charHeight
        }
    }

    /**
     * Rendert eine Zeile Monospace-Text in heightQ-Größe.
     * Für Code-Blöcke und Tabellen im Proportional-Modus.
     * Gleichmäßiger Zeichenabstand (Grid-Font), aber in variabler Zeilenhöhe.
     *
     * @param {CanvasRenderingContext2D} ctx
     * @param {string} text
     * @param {number} startX - Pixel-X
     * @param {number} pixelY - Pixel-Y
     * @param {Array} formatRuns - [{start, end, style, bold?, italic?}]
     * @param {number} heightQ - Zeilenhöhe in Quarterlines
     * @param {{start: number, end: number}|null} [selRange] - Selektionsbereich (Codepoint-Indizes)
     */
    renderMonoLineAtHeight(ctx, text, startX, pixelY, formatRuns, heightQ, selRange = null) {
        if (text.length === 0) return
        const localCharHeight = this.charHeightForHeightQ(heightQ)
        const localTopPad = this._glyphTopPad * heightQ / 4
        const localTextHeight = this.textHeightForHeightQ(heightQ)
        // Zeichenbreite skaliert mit heightQ (größere Zeichen bei höheren Zeilen)
        const monoCharWidth = this.charWidth * heightQ / 4
        const dpr = window.devicePixelRatio || 1
        const hasGlow = this.colors.glow.color !== null

        const codepoints = Array.from(text)
        const cpLen = codepoints.length

        // Selection-Hintergrund vor den Glyphs zeichnen — gleichmaessiger
        // monoCharWidth-Abstand macht das zu einem einfachen Rechteck.
        if (selRange && selRange.start < selRange.end) {
            const selStart = Math.max(selRange.start, 0)
            const selEnd = Math.min(selRange.end, cpLen)
            if (selStart < selEnd) {
                const bgColor = this._getHighlightBgColor(STYLES[Style.EDITOR_SELECTION])
                const x1 = startX + selStart * monoCharWidth
                const x2 = startX + selEnd * monoCharWidth
                const selVertOffset = this._verticalPad * heightQ / 4
                ctx.fillStyle = bgColor
                ctx.fillRect(x1, pixelY + selVertOffset, x2 - x1, localTextHeight)
            }
        }

        const runCount = formatRuns ? formatRuns.length : 0
        let runIdx = 0

        for (let i = 0; i < cpLen; i++) {
            if (codepoints[i] === ' ') continue

            while (runIdx < runCount && i >= formatRuns[runIdx].end) runIdx++
            const inRun = runIdx < runCount && i >= formatRuns[runIdx].start
            const styleName = inRun ? (formatRuns[runIdx].style || Style.EDITOR_TEXT) : Style.EDITOR_TEXT
            const resolved = this._resolveStyle(styleName)

            const pixelX = this._snapToPixel(startX + i * monoCharWidth)
            // Glyph bei Grid-Font-Größe erstellen (passt in charWidth), Höhe = heightQ
            const glyph = this._getProportionalGlyph(
                codepoints[i], resolved.color, resolved.isBold, resolved.isItalic,
                hasGlow && resolved.hasGlow, heightQ, true  // mono=true
            )
            const glyphW = glyph.width / dpr
            ctx.drawImage(glyph, pixelX, pixelY - localTopPad, glyphW, localCharHeight + localTopPad)
        }
    }

    /**
     * Rundet eine logische Koordinate auf das physische Pixel-Raster.
     * Verhindert Sub-Pixel-Versatz bei drawImage, der durch Nearest-Neighbor-
     * Interpolation (imageSmoothingEnabled=false) Glyphen aufdicken würde.
     *
     * @param {number} x - Logische Koordinate
     * @returns {number} Auf DPR-Raster gerundete Koordinate
     */
    _snapToPixel(x) {
        const dpr = window.devicePixelRatio || 1
        return Math.round(x * dpr) / dpr
    }

    /**
     * Berechnet die skalierte Strichbreite für Cursor, Underline, Strikethrough.
     *
     * @param {number} baseFactor - Basis-Faktor (z.B. 0.08 für Cursor)
     * @param {number} minWidth - Mindestbreite in Pixel
     * @returns {number}
     */
    _getScaledStrokeWidth(baseFactor, minWidth = 1) {
        return Math.max(minWidth, Math.round(this._gridFontSize * baseFactor))
    }

    /**
     * Rendert eine Zeile proportionalen Text mit Two-Box Kerning.
     *
     * @param {CanvasRenderingContext2D} ctx - Canvas-Context
     * @param {string} text - Der zu rendernde Text
     * @param {number} startX - Pixel-X des Textbereichs
     * @param {number} y - Pixel-Y der Zeile
     * @param {Array<{ start: number, end: number, bold: boolean, italic: boolean }>} formatRuns
     * @param {{ start: number, end: number }|null} selRange - Selektionsbereich (Zeichen-Indizes)
     * @param {object} [endMarker] - Optionaler End-Marker { char, selected }
     * @param {object} [precomputedLayout] - Vorberechnetes Layout (positions, totalWidth)
     */
    renderProportionalLine(ctx, text, startX, y, formatRuns, selRange, endMarker, precomputedLayout, heightQ = 4) {
        const { cache: activeCache, scaleFactor: activeScaleFactor } = this._activeMetrics(heightQ)
        if (!activeCache || text.length === 0) return

        const scale = activeScaleFactor * heightQ / 4
        const localCharHeight = this.charHeightForHeightQ(heightQ)
        const localTopPad = this._glyphTopPad * heightQ / 4
        const localTextHeight = this.textHeightForHeightQ(heightQ)
        const localFontSize = this.fontSizeForHeightQ(heightQ)
        const baseStep = activeCache.baseStep
        const dpr = window.devicePixelRatio || 1

        const hasGlow = this.colors.glow.color !== null

        // Codepoint-aware: Array.from() splittet Surrogate-Paare korrekt
        const codepoints = Array.from(text)
        const cpLen = codepoints.length

        // Pro Zeichen: Format bestimmen, Style auflösen — Cursor-basiert O(n+r)
        // Bold/Italic koennen entweder ueber den Style (z.B. EDITOR_BOLD via
        // parsedToFormatRuns kombiniert) ODER ueber formatRun.bold/italic
        // gesetzt werden — letzteres nutzen der Review-Composite-Pfad und der
        // Insert-Vorschau-Pfad, die einen Diff-Style (EDITOR_REVIEW_SHOWN) mit
        // Inline-Bold/Italic kombinieren wollen, ohne den Diff-Akzent-Style zu
        // verlieren. Glyph-Atlas-Key haengt direkt an isBold/isItalic, daher
        // sind beide Pfade kompatibel und cache-freundlich.
        const charFormats = new Array(cpLen)
        const runCount = formatRuns ? formatRuns.length : 0
        let runIdx = 0
        for (let i = 0; i < cpLen; i++) {
            // Run-Cursor vorrücken (formatRuns sind sortiert und nicht-überlappend)
            while (runIdx < runCount && i >= formatRuns[runIdx].end) runIdx++
            const inRun = runIdx < runCount && i >= formatRuns[runIdx].start
            const isUnderline = inRun ? (formatRuns[runIdx].underline || false) : false
            const isStrikethrough = inRun ? (formatRuns[runIdx].strikethrough || false) : false
            const isSpellWarning = inRun ? (formatRuns[runIdx].spellWarning || false) : false
            const isSpellWarningDim = inRun ? (formatRuns[runIdx].spellWarningDim || false) : false
            // Bold-Pfad mit expliziter Override-Semantik:
            //   formatRun.bold === undefined → Style-Default (kein Override)
            //   formatRun.bold === true|false → explizit, ueberschreibt Style
            // Damit ist `bold: false` ein echtes "kein Bold" auch ueber einem
            // Style mit isBold: true (z.B. MENU_ITEM_SELECTED). Wichtig fuer
            // den Dashboard-Pfad, wo das Layout (computeStringLayout) genau
            // diesen explicit-Wert nutzt — ohne den Render-Override wuerden
            // Glyphen bold gerendert auf schmalem Layout (Buchstaben kleben).
            // Italic/Mono behalten die additive Semantik (Editor-Pfade
            // verlassen sich darauf).
            const runBoldExplicit = inRun ? formatRuns[runIdx].bold : undefined
            const runBold = runBoldExplicit === true
            const runItalic = inRun ? (formatRuns[runIdx].italic || false) : false
            const runMono = inRun ? (formatRuns[runIdx].mono || false) : false
            const styleName = inRun ? (formatRuns[runIdx].style || Style.EDITOR_TEXT) : Style.EDITOR_TEXT
            const resolved = this._resolveStyle(styleName)
            const effectiveBold = runBoldExplicit === undefined ? resolved.isBold : runBoldExplicit
            const needsOverride = (effectiveBold !== resolved.isBold)
                || (runItalic && !resolved.isItalic)
                || (runMono && !resolved.isMono)
            const effective = needsOverride
                ? { ...resolved,
                    isBold: effectiveBold,
                    isItalic: resolved.isItalic || runItalic,
                    isMono: resolved.isMono || runMono }
                : resolved
            charFormats[i] = { isUnderline, isStrikethrough, isSpellWarning, isSpellWarningDim, resolved: effective }
        }

        // Layout berechnen (oder vorberechnetes Layout wiederverwenden)
        const layout = precomputedLayout || computeStringLayout(text, activeCache, baseStep, formatRuns)

        // Effektive linke Kanten pro Codepoint inkl. monoShift fuer
        // Inline-Mono-Runs. effLeft[cpLen] = rechtes Zeilenende. Wird sowohl
        // fuer Selection als auch fuer rightEdge genutzt — sonst landet das
        // Highlight bei Inline-Code an der proportionalen Position, die
        // Glyphs sitzen aber an der mono-shifted Position. Geteilte Geometrie
        // mit CoreView via textGeometry.js#buildMonoShiftedPositions (Lesson 82).
        const monoCharWidth = this.charWidth * heightQ / 4
        const effLeft = buildMonoShiftedPositions(
            layout, formatRuns, STYLES, scale, monoCharWidth, cpLen, startX
        )

        // Hilfsfunktion: rechte Kante eines Zeichens. Mitten in der Zeile =
        // effLeft des Folgezeichens. Beim letzten Zeichen: effLeft[cpLen]
        // (mono-aware: enthaelt monoShift bei Inline-Code, sonst = totalWidth).
        // Im rein-proportionalen Editor-Fall hatte hier frueher `positions[i] +
        // widths[i]` gestanden ("End-Marker uebernimmt die Erweiterung"); bei
        // Inline-Code am Zeilenende rutschte der End-Marker dann ins letzte
        // Wort. effLeft[cpLen] passt fuer beide Faelle (Lesson 88).
        const rightEdge = (i) => {
            if (i + 1 < cpLen) return effLeft[i + 1]
            return effLeft[cpLen]
        }

        // End-Marker Pixel-Position: effLeft[cpLen] enthaelt totalWidth +
        // monoShift, plus baseStep Abstand vor dem Marker.
        const markerPx = endMarker ? effLeft[cpLen] + baseStep * scale : 0
        const markerWidth = endMarker ? this.markerWidthPx(endMarker.heightQ || LINE_HEIGHT_Q.NORMAL) : 0

        // Selection-Hintergrund als ein Rechteck (Text + ggf. End-Marker)
        // Vertikal zentriert in der Zeile (wie der Text)
        const selVertOffset = this._verticalPad * heightQ / 4
        if (selRange && selRange.start < selRange.end) {
            const selStart = Math.max(selRange.start, 0)
            const selEnd = Math.min(selRange.end, cpLen)
            if (selStart < selEnd) {
                const bgColor = this._getHighlightBgColor(STYLES[Style.EDITOR_SELECTION])
                const x1 = effLeft[selStart]
                let x2 = rightEdge(selEnd - 1)
                // End-Marker nahtlos in Selection einbeziehen
                if (endMarker && endMarker.selected && selEnd === cpLen) {
                    x2 = markerPx + markerWidth
                }
                ctx.fillStyle = bgColor
                ctx.fillRect(x1, y + selVertOffset, x2 - x1, localTextHeight)
            }
        }

        // End-Marker Selection (wenn Text nicht selektiert, aber Marker schon)
        if (endMarker && endMarker.selected && (!selRange || selRange.end < cpLen)) {
            ctx.fillStyle = this._getHighlightBgColor(STYLES[Style.EDITOR_SELECTION])
            // Marker-Selection: NORMAL-Höhe (6QL), Baseline-aligned
            const selBaselineShift = heightQ > 6
                ? (this._verticalPad * (heightQ - 6) / 4
                    + (this.fontSizeForHeightQ(heightQ) - this.fontSizeForHeightQ(6)) * 0.85)
                : 0
            ctx.fillRect(markerPx, y + selVertOffset + selBaselineShift, markerWidth, this.textHeightForHeightQ(6))
        }

        // Glyphs rendern
        // Inline-Code-Runs: Mono-Font + gleichmäßiger charWidth-Abstand
        // monoShift: kumulativer Pixel-Offset durch Mono-Runs (Differenz Mono vs. Proportional-Breite)
        // monoCharWidth ist bereits oben fuer die effLeft-Berechnung definiert.
        let monoRunStart = -1
        let monoRunPixelX = 0
        let monoShift = 0

        for (let i = 0; i < cpLen; i++) {
            const fmt = charFormats[i]
            const useMono = fmt.resolved.isMono || false

            // Mono-Run-Tracking
            if (useMono && monoRunStart < 0) {
                // Neuer Mono-Run beginnt — Start-Position inkl. bisherigem Shift
                monoRunStart = i
                monoRunPixelX = startX + layout.positions[i] * scale + monoShift
            } else if (!useMono && monoRunStart >= 0) {
                // Mono-Run endet — Shift aktualisieren:
                // Differenz zwischen tatsächlicher Mono-Endposition und proportionaler Endposition
                const monoEndX = monoRunPixelX + (i - monoRunStart) * monoCharWidth
                const propEndX = startX + layout.positions[i] * scale + monoShift
                monoShift += (monoEndX - propEndX)
                monoRunStart = -1
            }

            if (codepoints[i] === ' ') continue

            let pixelX
            if (useMono && monoRunStart >= 0) {
                pixelX = monoRunPixelX + (i - monoRunStart) * monoCharWidth
            } else {
                pixelX = startX + layout.positions[i] * scale + monoShift
            }

            const glyph = this._getProportionalGlyph(codepoints[i], fmt.resolved.color, fmt.resolved.isBold, fmt.resolved.isItalic, hasGlow && fmt.resolved.hasGlow, heightQ, useMono)
            const glyphLogicalWidth = glyph.width / dpr
            ctx.drawImage(glyph, this._snapToPixel(pixelX), y - localTopPad, glyphLogicalWidth, localCharHeight + localTopPad)
        }

        // End-Marker Glyph rendern — Baseline-aligned zum Text. Default 6QL,
        // Note/Info reichen heightQ: 4 durch (kleiner, System-Font).
        if (endMarker) {
            const MARKER_HQ = endMarker.heightQ || LINE_HEIGHT_Q.NORMAL
            // endMarker.style erlaubt EDITOR_MARKER vs EDITOR_MARKER_DIM (Active-Field-Highlight)
            const markerResolved = this._resolveStyle(endMarker.style || Style.EDITOR_MARKER)
            const glyph = this._getProportionalGlyph(endMarker.char, markerResolved.color, false, false, false, MARKER_HQ)
            const glyphLogicalWidth = glyph.width / dpr
            const markerCharH = this.charHeightForHeightQ(MARKER_HQ)
            const markerTopPad = this._glyphTopPad * MARKER_HQ / 4
            // Baseline-Shift: Marker vertikal verschieben wenn Zeile größer ODER kleiner als 6QL.
            // Größer: Baseline an Text-Baseline der größeren Zeile angleichen.
            // Kleiner (z.B. 4QL Code-Block-Para): 6QL-Marker würde nach unten überstehen,
            // 1QL nach oben anheben.
            const baselineShift = heightQ > MARKER_HQ
                ? (this._verticalPad * (heightQ - MARKER_HQ) / 4
                    + (this.fontSizeForHeightQ(heightQ) - this.fontSizeForHeightQ(MARKER_HQ)) * 0.85)
                : heightQ < MARKER_HQ
                    ? -this.charHeight / 4
                    : 0
            ctx.drawImage(glyph, this._snapToPixel(markerPx), y + baselineShift - markerTopPad, glyphLogicalWidth, markerCharH + markerTopPad)
        }

        // Underline/Strikethrough — Y-Positionen mit vertikaler Zentrierung
        const localVertPad = this._verticalPad * heightQ / 4
        const localBaselineY = localVertPad + localFontSize * 0.85
        const localUnderlineY = localBaselineY + localFontSize * 0.1
        const xHeight = localFontSize * 0.52
        const localStrikethroughY = localBaselineY - xHeight * 0.5

        // Underline-Runs
        let ulStart = -1
        for (let i = 0; i <= cpLen; i++) {
            const isUL = i < cpLen && charFormats[i].isUnderline
            if (isUL && ulStart < 0) ulStart = i
            if (!isUL && ulStart >= 0) {
                const color = charFormats[ulStart].resolved.color
                const w = this._getScaledStrokeWidth(0.05, 1)
                const ulY = y + localUnderlineY
                ctx.strokeStyle = color
                ctx.lineWidth = w
                ctx.beginPath()
                ctx.moveTo(startX + layout.positions[ulStart] * scale, ulY)
                ctx.lineTo(rightEdge(i - 1), ulY)
                ctx.stroke()
                ulStart = -1
            }
        }

        // Strikethrough-Runs
        let stStart = -1
        for (let i = 0; i <= cpLen; i++) {
            const isST = i < cpLen && charFormats[i].isStrikethrough
            if (isST && stStart < 0) stStart = i
            if (!isST && stStart >= 0) {
                const color = charFormats[stStart].resolved.color
                const w = this._getScaledStrokeWidth(0.06, 1)
                const stY = y + localStrikethroughY
                ctx.strokeStyle = color
                ctx.lineWidth = w
                ctx.beginPath()
                ctx.moveTo(startX + layout.positions[stStart] * scale, stY)
                ctx.lineTo(rightEdge(i - 1), stY)
                ctx.stroke()
                stStart = -1
            }
        }

        // Spellcheck Wavy-Underline (additiv, nach Underline + Strikethrough).
        // Wir fassen alle Runs mit spellWarning=true zu Pfaden zusammen, dann
        // ein einziger ctx.stroke() pro Range. Mode-abhaengige Farbe.
        // x0/x1 BEIDE ueber effLeft (mono-aware) — sonst rutscht der Start
        // proportional weiter nach links, wenn vor dem Wort ein Inline-Code-
        // Span steht. effLeft[i] enthaelt den kumulativen monoShift bereits.
        // dim=true (Absatz nicht aktiv) → globalAlpha reduzieren, damit das
        // aktive Wort visuell dominiert.
        let swStart = -1
        let swDim = false
        for (let i = 0; i <= cpLen; i++) {
            const isSW = i < cpLen && charFormats[i].isSpellWarning
            if (isSW && swStart < 0) {
                swStart = i
                swDim = charFormats[i].isSpellWarningDim
            }
            if (!isSW && swStart >= 0) {
                const wavyColor = this._getSpellcheckWavyColor()
                const x0 = effLeft[swStart]
                const x1 = rightEdge(i - 1)
                // Welle 1.5px unter der regulaeren Underline-Linie, damit sich
                // beide bei Section-Links nicht ueberlagern.
                const waveY = y + localUnderlineY + this._getScaledStrokeWidth(0.05, 1.5)
                const dimAlpha = COLORS.SPELLCHECK_WAVY_INACTIVE_ALPHA
                if (swDim && dimAlpha < 1) {
                    ctx.save()
                    ctx.globalAlpha = (ctx.globalAlpha ?? 1) * dimAlpha
                    this._drawWavyUnderline(ctx, x0, x1, waveY, wavyColor, scale)
                    ctx.restore()
                } else {
                    this._drawWavyUnderline(ctx, x0, x1, waveY, wavyColor, scale)
                }
                swStart = -1
                swDim = false
            }
        }
    }

    /**
     * Spellcheck-Wavy-Underline im Monospace-Pfad.
     *
     * Wird vom Mono-Render-Body in EditorView aufgerufen, nachdem die Glyphen
     * der Zeile gesetzt wurden. Berechnet Pixel-Positionen aus Grid-Koordinaten
     * und delegiert an `_drawWavyUnderline` (selber Helper wie Proportional).
     * Mode-abhaengige Farbe via `_getSpellcheckWavyColor`.
     *
     * @param {number} gridX - Start-Spalte (Grid)
     * @param {number} gridY - Zeile (Grid)
     * @param {number} gridW - Breite in Spalten (>=1, beruecksichtigt Wide-Chars)
     */
    drawSpellWavyMonospace(gridX, gridY, gridW, dim = false) {
        if (gridW <= 0 || this.charWidth <= 0 || this.charHeight <= 0) return
        const x0 = gridX * this.charWidth
        const x1 = (gridX + gridW) * this.charWidth
        // Welle leicht unter der regulaeren Underline-Linie (analog Proportional-Pfad).
        const waveY = gridY * this.charHeight + this._underlineY + this._getScaledStrokeWidth(0.05, 1.5)
        const color = this._getSpellcheckWavyColor()
        // scale skaliert Amplitude/Periode der Welle. Wir nutzen den globalen
        // _scaleFactor (Zoom), nicht charWidth — sonst kommt eine Riesenwelle
        // mit nur ~4 Schwuengen pro Wort raus.
        const scale = Math.max(1, this._scaleFactor)
        const ctx = this.ctx
        const dimAlpha = COLORS.SPELLCHECK_WAVY_INACTIVE_ALPHA
        if (dim && dimAlpha < 1) {
            ctx.save()
            ctx.globalAlpha = (ctx.globalAlpha ?? 1) * dimAlpha
            this._drawWavyUnderline(ctx, x0, x1, waveY, color, scale)
            ctx.restore()
        } else {
            this._drawWavyUnderline(ctx, x0, x1, waveY, color, scale)
        }
    }

    /**
     * Liefert die Wavy-Underline-Farbe abhaengig vom aktiven Display-Mode.
     * Ultra/Regular Nerd → Phosphor-Gruen. Sonst rot (dark/light).
     * @private
     */
    _getSpellcheckWavyColor() {
        const mode = DISPLAY_MODES[this.currentDisplayMode]
        if (mode && mode.isNerd && !mode.isColored) {
            return COLORS.SPELLCHECK_WAVY_NERD
        }
        return this.colors && this.colors.isDarkMode
            ? COLORS.SPELLCHECK_WAVY_DARK
            : COLORS.SPELLCHECK_WAVY_LIGHT
    }

    /**
     * Zeichnet eine Sinus-Wellenlinie zwischen x0 und x1 auf Hoehe y.
     * Amplitude und Frequenz skalieren mit der aktuellen Glyph-Groesse.
     * @private
     */
    _drawWavyUnderline(ctx, x0, x1, y, color, scale) {
        if (x1 <= x0) return
        const amp     = Math.max(1.0, scale * 1.5)        // Amplitude
        const period  = Math.max(3.0, scale * 4.0)        // Periode
        const stroke  = this._getScaledStrokeWidth(0.05, 1)
        ctx.strokeStyle = color
        ctx.lineWidth   = stroke
        ctx.beginPath()
        ctx.moveTo(x0, y)
        // Sinus per quadratischer Approximation: pro Halbperiode ein quad-Curve
        // (Kontrollpunkt oberhalb/unterhalb). Schnell und visuell als Welle
        // wahrgenommen.
        let direction = 1
        for (let x = x0; x < x1; x += period / 2) {
            const xNext = Math.min(x + period / 2, x1)
            const xCtrl = (x + xNext) / 2
            const yCtrl = y + direction * amp
            ctx.quadraticCurveTo(xCtrl, yCtrl, xNext, y)
            direction = -direction
        }
        ctx.stroke()
    }

    /**
     * Markiert den Border-Cache als dirty.
     * Wird aufgerufen bei Layout-Änderungen, Hover auf Controls, etc.
     */
    markBorderDirty() {
        this._borderDirty = true
    }

    /**
     * Zeichnet den Border-Layer neu (wenn dirty) und kopiert ihn aufs Hauptcanvas.
     * Wird vom Scroll-Shift-Pfad genutzt, um den Border zu aktualisieren ohne
     * einen Full-Render erzwingen zu müssen (z.B. Label-Wechsel beim Separator
     * am oberen Pane-Rand).
     *
     * @param {LayoutManager} layout
     */
    renderBorderLayer(layout) {
        this._renderLayout(layout)
    }
}
